/**
 * Market Intelligence & Map Module — v2 "Masterpiece"
 *
 * GIS Command Center for branch performance tracking and competitor mapping.
 * Features: Smart Pin Logos, Target Management, Actuals Entry, TV Command Mode,
 * Performance Heatmap, Clean Architecture.
 * Tabs: Dashboard, Live Map, Location Manager, Target Management, Settings.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import type { User } from '../../domain/entities';
import { isPlatformRole } from '../../domain/entities';
import { ModuleTabs, TabGate } from '../shared/ModuleTabs';
import { hasTabVariant } from '../../core/security';
import { cn } from '../../ui/utils';
import { useToast } from '../../ui/Toast';
import { isRemoteMode } from '../../config/repository';
import { apiFetch } from '../../data/ApiModuleRepository';
import {
  MapPin, Plus, Search, Edit2, Trash2, X, Check,
  Download, Upload, BarChart3, Settings as SettingsIcon,
  Navigation, Layers, Target, DollarSign, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle2, RefreshCw,
  Building2, Flag, Save, Globe, ToggleLeft, ToggleRight, Loader2,
  LayoutGrid, Trophy, Shield, Circle, Bot, Eye,
  Lock, Unlock, Calendar, ChevronDown, ChevronRight, Archive,
  BarChart2, Filter, Activity, Radar, Star, Info, Clock, History, Monitor,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMarketBI } from './useMarketBI';
import type { DailyHistoryEntry } from './useMarketBI';
import { usePersistentState } from '../../hooks/usePersistentState';
import { ManagerFocusDashboard } from './ManagerFocusDashboard';
import { AdminCommandDashboard } from './AdminCommandDashboard';
import { AdminSalesHistory } from './AdminSalesHistory';
import { LiveMap } from './components/LiveMap';

// ═══════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════

interface MapLocation {
  id: string;
  org_id: string;
  brand_id: string;
  branch_id: string;
  name: string;
  type: 'INTERNAL' | 'COMPETITOR';
  lat: number;
  lon: number;
  daily_target: number;
  monthly_target: number;
  competitor_tag: string;
  competitor_tag_id: string;
  description: string;
  address: string;
  icon_color: string;
  created_at: number;
  updated_at: number;
  // Joined from sales summary
  current_sales?: number;
  entered_by?: string;
  entered_name?: string;
  sales_updated_at?: number;
  brand_name?: string;
  brand_logo_url?: string;
  branch_name?: string;
  branch_code?: string;
  effective_daily_target?: number;
  effective_monthly_target?: number;
  daily_override?: number;
  service_radius?: number;
  weighted_target_factor?: number;
  google_rating?: number;
  google_place_id?: string;
  // Joined from competitor_tags
  tag_name?: string;
  tag_logo_url?: string;
  tag_color?: string;
}

/** Scouted competitor from Google Places (not yet saved) */
interface ScoutedCompetitor {
  name: string;
  lat: number;
  lng: number;
  rating: number;
  userRatingsTotal: number;
  placeId: string;
  vicinity: string;
}

interface MapSettings {
  org_id: string;
  default_center_lat: number;
  default_center_lon: number;
  default_zoom: number;
  show_internal: number;
  show_competitors: number;
  show_performance: number;
  dark_theme: number;
  tv_layout: 'landscape' | 'portrait';
  map_style: 'dark' | 'silver';
  default_service_radius: number;
  target_calc_rule: 'fixed' | 'weighted';
  auto_cycle_enabled: number;
  auto_cycle_interval: number;
  ai_google_places: number;
  ai_predictive_sales: number;
  spotlight_enabled: number;
  spotlight_duration: number;
  show_sales_events: number;
  show_competitor_events: number;
  tv_orientation: 'landscape' | 'portrait';
  war_room_zoom: number;
  spotlight_zoom: number;
}

interface BrandInfo {
  id: string;
  name: string;
  logo_url?: string;
}

interface BranchInfo {
  id: string;
  name: string;
  code: string;
  brand_id: string;
}

interface CompetitorTag {
  id: string;
  org_id: string;
  name: string;
  logo_url: string;
  color: string;
  sort_order: number;
  created_at: number;
  updated_at: number;
}

const COMPETITOR_TAGS_FALLBACK = [
  'Pizza Rival', 'Burger Rival', 'Chicken Rival', 'Coffee Rival',
  'Fast Food Rival', 'Bakery Rival', 'Shawarma Rival', 'Fine Dining',
  'QSR Chain', 'Local Competitor', 'Other',
];

const DEFAULT_SETTINGS: MapSettings = {
  org_id: '',
  default_center_lat: 25.2854,
  default_center_lon: 51.5310,
  default_zoom: 11,
  show_internal: 1,
  show_competitors: 1,
  show_performance: 1,
  dark_theme: 1,
  tv_layout: 'landscape',
  map_style: 'dark',
  default_service_radius: 3000,
  target_calc_rule: 'fixed',
  auto_cycle_enabled: 0,
  auto_cycle_interval: 60,
  ai_google_places: 0,
  ai_predictive_sales: 0,
  spotlight_enabled: 1,
  spotlight_duration: 8,
  show_sales_events: 1,
  show_competitor_events: 1,
  tv_orientation: 'landscape',
  war_room_zoom: 12,
  spotlight_zoom: 16,
};

// ═══════════════════════════════════════════════════════════
// PERFORMANCE HELPERS
// ═══════════════════════════════════════════════════════════

function getEffectiveTarget(loc: MapLocation): number {
  return loc.effective_daily_target ?? loc.daily_target ?? 0;
}

function getSalesRatio(loc: MapLocation): number {
  const target = getEffectiveTarget(loc);
  if (!target || target <= 0) return 0;
  return (loc.current_sales ?? 0) / target;
}

function getPerformanceColor(ratio: number): string {
  if (ratio >= 0.8) return '#10b981';     // emerald — ≥80%
  if (ratio >= 0.4) return '#f59e0b';     // amber — 40-80%
  return '#ef4444';                        // red — <40%
}

function getPerformanceLabel(ratio: number): string {
  if (ratio >= 1) return 'On Target';
  if (ratio >= 0.8) return 'Near Target';
  if (ratio >= 0.4) return 'Below Target';
  return 'Critical';
}

function getPulseSpeed(ratio: number): string {
  if (ratio >= 0.8) return '3s';  // slow
  if (ratio >= 0.4) return '2s';  // medium
  return '1s';                     // fast — urgent!
}

function formatCurrency(n: number): string {
  return n.toLocaleString('en-QA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

/** Haversine distance in meters between two lat/lon points */
function geoDistanceMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Get the monthly achievement ratio (for trophy calculation) */
function getMonthlyRatio(loc: MapLocation): number {
  const monthlyTarget = loc.effective_monthly_target || loc.monthly_target || 0;
  if (monthlyTarget <= 0) return 0;
  // We approximate monthly sales from daily: current_sales * 30 (rough)
  // In reality this would come from sum of all daily entries for the month
  return (loc.current_sales ?? 0) / (getEffectiveTarget(loc) || 1);
}

function getMediaUrl(url?: string): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  const API_BASE = (import.meta as any).env?.VITE_API_URL ?? '';
  const token = localStorage.getItem('dm_jwt');
  const q = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${API_BASE}${url}${q}`;
}

// ═══════════════════════════════════════════════════════════
// MAP TILES
// ═══════════════════════════════════════════════════════════

const SILVER_TILE = 'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png';
const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>';

// ═══════════════════════════════════════════════════════════
// MAIN MODULE EXPORT
// ═══════════════════════════════════════════════════════════

export function MarketMapModule({ user }: { user: User | null }) {
  const basePath = '/apps/market-map';
  const tabs = [
    { key: 'dashboard', label: 'Dashboard', icon: <BarChart3 size={14} />, to: basePath, exact: true },
    { key: 'live-map', label: 'Live Map', icon: <Navigation size={14} />, to: `${basePath}/live-map` },
    { key: 'location-manager', label: 'Location Manager', icon: <MapPin size={14} />, to: `${basePath}/location-manager` },
    { key: 'target-management', label: 'Target Management', icon: <Target size={14} />, to: `${basePath}/target-management` },
    { key: 'sales-history', label: 'Sales History', icon: <History size={14} />, to: `${basePath}/sales-history` },
    { key: 'analytics', label: 'Analytics', icon: <BarChart2 size={14} />, to: `${basePath}/analytics` },
    { key: 'archive', label: 'Intelligence Archive', icon: <Archive size={14} />, to: `${basePath}/archive` },
    { key: 'settings', label: 'Settings', icon: <SettingsIcon size={14} />, to: `${basePath}/settings` },
  ];

  return (
    <div className="max-w-[1600px] mx-auto">
      <ModuleTabs moduleId="market-map" tabs={tabs} user={user} />
      <div className="p-6">
        <Routes>
          <Route index element={<TabGate moduleId="market-map" tabId="dashboard" user={user}><DashboardTab user={user} /></TabGate>} />
          <Route path="live-map" element={<TabGate moduleId="market-map" tabId="live-map" user={user}><LiveMap user={user} /></TabGate>} />
          <Route path="location-manager" element={<TabGate moduleId="market-map" tabId="location-manager" user={user}><LocationManagerTab user={user} /></TabGate>} />
          <Route path="target-management" element={<TabGate moduleId="market-map" tabId="target-management" user={user}><TargetManagementTab user={user} /></TabGate>} />
          <Route path="sales-history" element={<TabGate moduleId="market-map" tabId="sales-history" user={user}><AdminSalesHistory user={user} /></TabGate>} />
          <Route path="analytics" element={<TabGate moduleId="market-map" tabId="analytics" user={user}><AnalyticsDashboardTab user={user} /></TabGate>} />
          <Route path="archive" element={<TabGate moduleId="market-map" tabId="archive" user={user}><IntelligenceArchiveTab user={user} /></TabGate>} />
          <Route path="settings" element={<TabGate moduleId="market-map" tabId="settings" user={user}><SettingsTab user={user} /></TabGate>} />
        </Routes>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// 1. DASHBOARD TAB — Hard Split by Role
// ═══════════════════════════════════════════════════════════

function DashboardTab({ user }: { user: User | null }) {
  const role = user?.role;
  if (role === 'SUPER_ADMIN' || role === 'ADMIN' || role === 'PLATFORM_OWNER') {
    return <AdminCommandDashboard user={user} />;
  }
  return <ManagerFocusDashboard user={user} />;
}

// ═══════════════════════════════════════════════════════════
// 2. LIVE MAP TAB — Moved to components/LiveMap.tsx
// ═══════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════
// 3. LOCATION MANAGER TAB
// ═══════════════════════════════════════════════════════════

function LocationManagerTab({ user }: { user: User | null }) {
  const isAdmin = hasTabVariant(user, 'market-map', 'location-manager', 'admin');
  const toast = useToast();
  const [locations, setLocations] = useState<MapLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<'ALL' | 'INTERNAL' | 'COMPETITOR'>('ALL');
  const [editingLoc, setEditingLoc] = useState<MapLocation | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [salesInputs, setSalesInputs] = useState<Record<string, string>>({});
  const [savingSales, setSavingSales] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [importMode, setImportMode] = useState<'append' | 'replace' | 'update'>('append');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);

  const loadLocations = useCallback(async () => {
    if (!isRemoteMode()) { setLoading(false); return; }
    setError(false);
    try {
      const res = await apiFetch('/api/map/sales/summary');
      setLocations((res as any).data ?? []);
    } catch { setError(true); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadLocations(); }, [loadLocations]);

  const filtered = useMemo(() => {
    return locations.filter(l => {
      if (filterType !== 'ALL' && l.type !== filterType) return false;
      if (search && !l.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [locations, filterType, search]);

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this location and all its sales data?')) return;
    try {
      await apiFetch(`/api/map/locations/${id}`, { method: 'DELETE' });
      toast.success('Deleted', 'Location removed.');
      loadLocations();
    } catch (e: any) { toast.error('Failed', e.message || 'Delete failed.'); }
  };

  const handleSalesSubmit = async (locId: string) => {
    const val = parseFloat(salesInputs[locId] || '0');
    if (!val || val <= 0) { toast.warning('Invalid', 'Enter a valid sales amount.'); return; }
    setSavingSales(locId);
    try {
      await apiFetch('/api/map/sales', { method: 'POST', body: JSON.stringify({ locationId: locId, salesAmount: val }) });
      toast.success('Saved', `Sales entry recorded.`);
      setSalesInputs(prev => ({ ...prev, [locId]: '' }));
      loadLocations();
    } catch (e: any) { toast.error('Failed', e.message || 'Save failed.'); }
    finally { setSavingSales(null); }
  };

  // Excel Export
  const handleExport = async () => {
    const XLSX = await import('xlsx-js-style');
    const aoa = [
      ['Branch Name', 'Branch Code', 'Name', 'Type', 'Brand', 'Latitude', 'Longitude', 'Daily Target', 'Monthly Target', 'Competitor Tag', 'Description', 'Address'],
      ...locations.map(l => [l.branch_name ?? '', l.branch_code ?? '', l.name, l.type, l.brand_name ?? '', l.lat, l.lon, l.daily_target, l.monthly_target, l.tag_name || l.competitor_tag, l.description, l.address]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    // Style header
    for (let ci = 0; ci < aoa[0].length; ci++) {
      const cell = ws[XLSX.utils.encode_cell({ r: 0, c: ci })];
      if (cell) cell.s = { fill: { fgColor: { rgb: 'FF1e293b' } }, font: { bold: true, color: { rgb: 'FFFFFFFF' } }, alignment: { horizontal: 'center' } };
    }
    ws['!cols'] = [{ wch: 22 }, { wch: 14 }, { wch: 25 }, { wch: 12 }, { wch: 18 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 30 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Locations');
    XLSX.writeFile(wb, 'Market_Map_Locations.xlsx');
    toast.success('Exported', `${locations.length} locations exported.`);
  };

  // Excel Import — stage file then show modal
  const handleImportFileSelect = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setImportMode('append');
    setShowImportModal(true);
    evt.target.value = '';
  };

  const handleImportConfirm = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const XLSX = await import('xlsx-js-style');
      const buf = await importFile.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<Record<string, any>>(ws);

      if (rows.length === 0) { toast.warning('Empty', 'No data found in the file.'); return; }
      if (rows.length > 200) { toast.error('Too Many', 'Max 200 locations per import.'); return; }

      const locationsToImport = rows.map(r => ({
        name: String(r['Name'] || r['name'] || ''),
        type: String(r['Type'] || r['type'] || 'INTERNAL').toUpperCase(),
        branchCode: String(r['Branch Code'] || r['branch_code'] || ''),
        brandId: String(r['Brand ID'] || r['brand_id'] || r['Brand'] || ''),
        competitorTag: String(r['Competitor Tag'] || r['competitor_tag'] || ''),
        lat: Number(r['Latitude'] || r['lat'] || 0),
        lon: Number(r['Longitude'] || r['lon'] || 0),
        dailyTarget: Number(r['Daily Target'] || r['daily_target'] || 0),
        monthlyTarget: Number(r['Monthly Target'] || r['monthly_target'] || 0),
        description: String(r['Description'] || r['description'] || ''),
        address: String(r['Address'] || r['address'] || ''),
      })).filter(l => l.name && l.lat && l.lon);

      if (locationsToImport.length === 0) { toast.warning('Invalid', 'No valid rows with Name + Lat + Lon.'); return; }

      await apiFetch('/api/map/locations/bulk', {
        method: 'POST',
        body: JSON.stringify({ locations: locationsToImport, mode: importMode }),
      });

      const modeLabel = importMode === 'replace' ? 'Replaced' : importMode === 'update' ? 'Updated' : 'Appended';
      toast.success(modeLabel, `${locationsToImport.length} locations ${modeLabel.toLowerCase()}.`);
      loadLocations();
    } catch (e: any) { toast.error('Import Failed', e.message || 'Could not parse the file.'); }
    finally { setImporting(false); setShowImportModal(false); setImportFile(null); setShowImport(false); }
  };

  // Template Download
  const handleDownloadTemplate = async () => {
    const XLSX = await import('xlsx-js-style');
    const aoa = [
      ['Branch Code', 'Name', 'Type', 'Latitude', 'Longitude', 'Daily Target', 'Monthly Target', 'Competitor Tag', 'Description', 'Address'],
      ['AWK-01', 'Al Wakra Branch', 'INTERNAL', '25.1716', '51.6040', '5000', '150000', '', 'Main street location', 'Al Wakra, Qatar'],
      ['', 'Competitor Cafe', 'COMPETITOR', '25.2900', '51.5300', '', '', 'Coffee Rival', 'Downtown competitor', 'West Bay, Qatar'],
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    for (let ci = 0; ci < aoa[0].length; ci++) {
      const cell = ws[XLSX.utils.encode_cell({ r: 0, c: ci })];
      if (cell) cell.s = { fill: { fgColor: { rgb: 'FF1e293b' } }, font: { bold: true, color: { rgb: 'FFFFFFFF' } }, alignment: { horizontal: 'center' } };
    }
    ws['!cols'] = [{ wch: 14 }, { wch: 25 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 30 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'Market_Map_Template.xlsx');
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <Loader2 size={28} className="animate-spin text-indigo-400" />
        <p className="text-sm text-slate-400 font-medium">Loading locations…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <AlertTriangle size={28} className="text-amber-400" />
        <p className="text-sm text-slate-500 font-medium">Failed to load locations — database may be warming up.</p>
        <button onClick={() => { setLoading(true); loadLocations(); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition-colors">
          <RefreshCw size={13} /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Location Manager</h2>
          <p className="text-sm text-slate-500 mt-1">{locations.length} locations · {locations.filter(l => l.type === 'INTERNAL').length} branches · {locations.filter(l => l.type === 'COMPETITOR').length} competitors</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={handleDownloadTemplate} className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:border-indigo-300 transition-colors">
              <Download size={13} /> Template
            </button>
            <label className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:border-indigo-300 transition-colors cursor-pointer">
              <Upload size={13} /> Import
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleImportFileSelect} className="hidden" />
            </label>
            <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:border-indigo-300 transition-colors">
              <Download size={13} /> Export
            </button>
            <button onClick={() => { setEditingLoc(null); setShowAdd(true); }}
              className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition-colors">
              <Plus size={13} /> Add Location
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search locations…"
            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:border-indigo-300 focus:ring-1 focus:ring-indigo-200 outline-none" />
        </div>
        <div className="flex bg-white border border-slate-200 rounded-lg overflow-hidden">
          {(['ALL', 'INTERNAL', 'COMPETITOR'] as const).map(t => (
            <button key={t} onClick={() => setFilterType(t)}
              className={cn('px-3 py-2 text-xs font-bold transition-colors', filterType === t ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-50')}>
              {t === 'ALL' ? 'All' : t === 'INTERNAL' ? 'Branches' : 'Competitors'}
            </button>
          ))}
        </div>
      </div>

      {/* Location Cards */}
      <div className="space-y-2">
        {filtered.map(loc => {
          const ratio = getSalesRatio(loc);
          const color = loc.type === 'INTERNAL' ? getPerformanceColor(ratio) : '#64748b';
          const target = getEffectiveTarget(loc);
          const logoUrl = getMediaUrl(loc.brand_logo_url);

          return (
            <div key={loc.id} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-4">
              {/* Logo/Icon */}
              {loc.type === 'INTERNAL' && logoUrl ? (
                <img src={logoUrl} alt="" className="w-10 h-10 rounded-full object-cover border-2 shrink-0" style={{ borderColor: color }} />
              ) : (
                <div className={cn('w-10 h-10 rounded-full flex items-center justify-center shrink-0',
                  loc.type === 'COMPETITOR' ? 'bg-red-50 border-2 border-red-200' : 'bg-indigo-50')}>
                  {loc.type === 'COMPETITOR' ? <Flag size={16} className="text-red-400" /> : <Building2 size={16} className="text-indigo-400" />}
                </div>
              )}

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-slate-800 truncate">{loc.name}</p>
                  {loc.type === 'COMPETITOR' && loc.competitor_tag && (
                    <span className="px-2 py-0.5 bg-red-50 text-red-500 text-[10px] font-bold rounded-full">{loc.competitor_tag}</span>
                  )}
                  {loc.brand_name && <span className="text-[10px] text-slate-400">{loc.brand_name}</span>}
                  {loc.branch_code && <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded font-mono">{loc.branch_code}</span>}
                </div>
                <p className="text-[11px] text-slate-400 truncate">{loc.address || `${loc.lat.toFixed(4)}, ${loc.lon.toFixed(4)}`}</p>
              </div>

              {/* Performance + Sales Entry (branches only) */}
              {loc.type === 'INTERNAL' && (
                <div className="flex items-center gap-4">
                  <div className="w-24">
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(ratio * 100, 100)}%`, backgroundColor: color }} />
                    </div>
                    <div className="flex justify-between mt-0.5">
                      <span className="text-[9px] text-slate-400">QAR {formatCurrency(loc.current_sales ?? 0)}</span>
                      <span className="text-[9px] font-bold" style={{ color }}>{target > 0 ? `${(ratio * 100).toFixed(0)}%` : '—'}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <input
                      type="number" placeholder="Sales…"
                      value={salesInputs[loc.id] || ''}
                      onChange={e => setSalesInputs(prev => ({ ...prev, [loc.id]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') handleSalesSubmit(loc.id); }}
                      className="w-24 px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:border-indigo-300 focus:ring-1 focus:ring-indigo-200 outline-none"
                    />
                    <button onClick={() => handleSalesSubmit(loc.id)}
                      disabled={savingSales === loc.id}
                      className="p-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50">
                      {savingSales === loc.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                    </button>
                  </div>
                </div>
              )}

              {/* Actions (admin) */}
              {isAdmin && (
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => { setEditingLoc(loc); setShowAdd(true); }}
                    className="p-1.5 hover:bg-indigo-50 rounded-lg transition-colors" title="Edit">
                    <Edit2 size={13} className="text-indigo-500" />
                  </button>
                  <button onClick={() => handleDelete(loc.id)}
                    className="p-1.5 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                    <Trash2 size={13} className="text-red-400" />
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
            <MapPin size={28} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm font-semibold text-slate-500">{search ? 'No matching locations.' : 'No locations yet.'}</p>
            {isAdmin && !search && (
              <button onClick={() => setShowAdd(true)}
                className="mt-3 px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition-colors">
                <Plus size={12} className="inline mr-1" /> Add Location
              </button>
            )}
          </div>
        )}
      </div>

      {/* Import Mode Modal */}
      <AnimatePresence>
        {showImportModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm"
            onClick={() => { setShowImportModal(false); setImportFile(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md mx-4 overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="px-6 py-5 border-b border-slate-100">
                <h3 className="text-lg font-bold text-slate-900">Import Mode</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Choose how to handle <span className="font-semibold text-slate-700">{importFile?.name}</span>
                </p>
              </div>
              <div className="p-6 space-y-3">
                {([
                  { value: 'append' as const, label: 'Append', icon: <Plus size={16} />, desc: 'Add new locations. Existing locations are untouched.', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
                  { value: 'update' as const, label: 'Update', icon: <RefreshCw size={16} />, desc: 'Match by name — update existing, add new ones.', color: 'text-blue-600 bg-blue-50 border-blue-200' },
                  { value: 'replace' as const, label: 'Replace', icon: <Trash2 size={16} />, desc: 'Delete ALL existing locations, then import from file.', color: 'text-red-600 bg-red-50 border-red-200' },
                ]).map(opt => (
                  <button key={opt.value} onClick={() => setImportMode(opt.value)}
                    className={cn(
                      'w-full flex items-start gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all',
                      importMode === opt.value
                        ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                        : 'border-slate-200 hover:border-slate-300'
                    )}>
                    <div className={cn('p-2 rounded-lg border shrink-0', opt.color)}>{opt.icon}</div>
                    <div>
                      <p className="text-sm font-bold text-slate-800">{opt.label}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">{opt.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
              {importMode === 'replace' && (
                <div className="mx-6 mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-[11px] text-red-600 font-semibold">⚠️ Warning: This will permanently delete all existing locations before importing.</p>
                </div>
              )}
              <div className="px-6 pb-5 flex items-center gap-3 justify-end">
                <button onClick={() => { setShowImportModal(false); setImportFile(null); }}
                  className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors">
                  Cancel
                </button>
                <button onClick={handleImportConfirm} disabled={importing}
                  className={cn(
                    'flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-bold text-white transition-colors disabled:opacity-50',
                    importMode === 'replace' ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'
                  )}>
                  {importing ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                  {importMode === 'replace' ? 'Replace All' : importMode === 'update' ? 'Update & Import' : 'Append'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add/Edit Drawer */}
      <AnimatePresence>
        {showAdd && (
          <LocationFormDrawer
            location={editingLoc}
            onClose={() => { setShowAdd(false); setEditingLoc(null); }}
            onSaved={() => { setShowAdd(false); setEditingLoc(null); loadLocations(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// LOCATION FORM DRAWER
// ═══════════════════════════════════════════════════════════

function LocationFormDrawer({
  location, onClose, onSaved,
}: {
  location: MapLocation | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const isEdit = !!location;
  const [brands, setBrands] = useState<BrandInfo[]>([]);
  const [branches, setBranches] = useState<BranchInfo[]>([]);
  const [competitorTags, setCompetitorTags] = useState<CompetitorTag[]>([]);
  const [form, setForm] = useState({
    name: location?.name ?? '',
    type: location?.type ?? 'INTERNAL' as 'INTERNAL' | 'COMPETITOR',
    brandId: location?.brand_id ?? '',
    branchId: location?.branch_id ?? '',
    lat: String(location?.lat ?? ''),
    lon: String(location?.lon ?? ''),
    dailyTarget: String(location?.daily_target ?? ''),
    monthlyTarget: String(location?.monthly_target ?? ''),
    competitorTag: location?.competitor_tag ?? '',
    competitorTagId: location?.competitor_tag_id ?? '',
    description: location?.description ?? '',
    address: location?.address ?? '',
    serviceRadius: String(location?.service_radius ?? ''),
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isRemoteMode()) return;
    (async () => {
      try {
        const [brandsRes, tagsRes] = await Promise.all([
          apiFetch('/api/map/brands-with-branches'),
          apiFetch('/api/map/competitor-tags'),
        ]);
        const data = (brandsRes as any).data;
        setBrands(data?.brands ?? []);
        setBranches(data?.branches ?? []);
        setCompetitorTags((tagsRes as any).data ?? []);
      } catch { /* ignore */ }
    })();
  }, []);

  const filteredBranches = useMemo(() => {
    if (!form.brandId) return branches;
    return branches.filter(b => b.brand_id === form.brandId);
  }, [branches, form.brandId]);

  const handleSubmit = async () => {
    if (!form.name) { toast.warning('Required', 'Name is required.'); return; }
    if (!form.lat || !form.lon) { toast.warning('Required', 'Latitude and Longitude are required.'); return; }

    setSaving(true);
    try {
      const payload = {
        name: form.name,
        type: form.type,
        brandId: form.brandId,
        branchId: form.branchId,
        lat: parseFloat(form.lat),
        lon: parseFloat(form.lon),
        dailyTarget: parseFloat(form.dailyTarget) || 0,
        monthlyTarget: parseFloat(form.monthlyTarget) || 0,
        competitorTag: form.competitorTag,
        competitorTagId: form.competitorTagId,
        description: form.description,
        address: form.address,
        serviceRadius: parseInt(form.serviceRadius) || 0,
      };

      if (isEdit) {
        await apiFetch(`/api/map/locations/${location!.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        toast.success('Updated', `${form.name} updated.`);
      } else {
        await apiFetch('/api/map/locations', { method: 'POST', body: JSON.stringify(payload) });
        toast.success('Created', `${form.name} added to map.`);
      }
      onSaved();
    } catch (e: any) { toast.error('Failed', e.message || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const upd = (key: string, val: string) => setForm(prev => ({ ...prev, [key]: val }));

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <motion.div
        initial={{ x: 400 }} animate={{ x: 0 }} exit={{ x: 400 }} transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="w-full max-w-md bg-white shadow-2xl h-full overflow-y-auto"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="text-base font-bold text-slate-900">{isEdit ? 'Edit Location' : 'Add Location'}</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg"><X size={16} className="text-slate-500" /></button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          {/* Type */}
          <div>
            <label className="text-xs font-bold text-slate-600 block mb-1.5">Type</label>
            <div className="flex gap-2">
              {(['INTERNAL', 'COMPETITOR'] as const).map(t => (
                <button key={t} onClick={() => upd('type', t)}
                  className={cn('flex-1 py-2 rounded-lg text-xs font-bold border transition-all',
                    form.type === t ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-500 border-slate-200 hover:border-indigo-300')}>
                  {t === 'INTERNAL' ? '🏢 Branch' : '🏴 Competitor'}
                </button>
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="text-xs font-bold text-slate-600 block mb-1.5">Name *</label>
            <input value={form.name} onChange={e => upd('name', e.target.value)}
              placeholder="e.g. Al Wakra Branch"
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:border-indigo-300 focus:ring-1 focus:ring-indigo-200 outline-none" />
          </div>

          {/* Branch-specific: Brand + Branch pickers */}
          {form.type === 'INTERNAL' && (
            <>
              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1.5">Brand (for Smart Pin Logo)</label>
                <select value={form.brandId} onChange={e => { upd('brandId', e.target.value); upd('branchId', ''); }}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:border-indigo-300 outline-none">
                  <option value="">— Select Brand —</option>
                  {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                {form.brandId && brands.find(b => b.id === form.brandId)?.logo_url && (
                  <div className="flex items-center gap-2 mt-2 p-2 bg-slate-50 rounded-lg">
                    <img src={getMediaUrl(brands.find(b => b.id === form.brandId)?.logo_url)} alt="" className="w-8 h-8 rounded-full object-cover border border-slate-200" />
                    <span className="text-xs text-slate-500">This logo will appear as the map pin</span>
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1.5">Branch</label>
                <select value={form.branchId} onChange={e => upd('branchId', e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:border-indigo-300 outline-none">
                  <option value="">— Select Branch —</option>
                  {filteredBranches.map(b => <option key={b.id} value={b.id}>{b.name} ({b.code})</option>)}
                </select>
              </div>
            </>
          )}

          {/* Competitor-specific: Tag */}
          {form.type === 'COMPETITOR' && (
            <div>
              <label className="text-xs font-bold text-slate-600 block mb-1.5">Competitor Tag</label>
              {competitorTags.length > 0 ? (
                <>
                  <select value={form.competitorTagId} onChange={e => {
                    const tagId = e.target.value;
                    const tag = competitorTags.find(t => t.id === tagId);
                    upd('competitorTagId', tagId);
                    upd('competitorTag', tag?.name ?? '');
                  }}
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:border-indigo-300 outline-none">
                    <option value="">— Select Tag —</option>
                    {competitorTags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  {form.competitorTagId && (() => {
                    const tag = competitorTags.find(t => t.id === form.competitorTagId);
                    if (!tag) return null;
                    return (
                      <div className="flex items-center gap-2 mt-2 p-2 bg-slate-50 rounded-lg">
                        {tag.logo_url ? (
                          <img src={getMediaUrl(tag.logo_url)} alt="" className="w-8 h-8 rounded-full object-cover border border-slate-200" />
                        ) : (
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-black" style={{ background: tag.color || '#ef4444' }}>
                            {tag.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className="text-xs text-slate-500">This logo will appear as the map pin</span>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <select value={form.competitorTag} onChange={e => upd('competitorTag', e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:border-indigo-300 outline-none">
                  <option value="">— Select Tag —</option>
                  {COMPETITOR_TAGS_FALLBACK.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              )}
            </div>
          )}

          {/* Coordinates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-slate-600 block mb-1.5">Latitude *</label>
              <input value={form.lat} onChange={e => upd('lat', e.target.value)} type="number" step="any"
                placeholder="25.2854"
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:border-indigo-300 focus:ring-1 focus:ring-indigo-200 outline-none" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 block mb-1.5">Longitude *</label>
              <input value={form.lon} onChange={e => upd('lon', e.target.value)} type="number" step="any"
                placeholder="51.5310"
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:border-indigo-300 focus:ring-1 focus:ring-indigo-200 outline-none" />
            </div>
          </div>

          {/* Targets (branches only) */}
          {form.type === 'INTERNAL' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1.5">Monthly Target (QAR)</label>
                <input value={form.monthlyTarget} onChange={e => upd('monthlyTarget', e.target.value)} type="number"
                  placeholder="150000"
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:border-indigo-300 focus:ring-1 focus:ring-indigo-200 outline-none" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1.5">Daily Target (QAR)</label>
                <input value={form.dailyTarget} onChange={e => upd('dailyTarget', e.target.value)} type="number"
                  placeholder={form.monthlyTarget ? String(Math.round(Number(form.monthlyTarget) / 30)) : '5000'}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:border-indigo-300 focus:ring-1 focus:ring-indigo-200 outline-none" />
                {form.monthlyTarget && !form.dailyTarget && (
                  <p className="text-[10px] text-slate-400 mt-1">Auto: {formatCurrency(Math.round(Number(form.monthlyTarget) / 30))} / day</p>
                )}
              </div>
            </div>
          )}

          {/* Service Radius Override (branches only) */}
          {form.type === 'INTERNAL' && (
            <div>
              <label className="text-xs font-bold text-slate-600 block mb-1.5">Service Radius (meters)</label>
              <input value={form.serviceRadius} onChange={e => upd('serviceRadius', e.target.value)} type="number"
                placeholder="0 = use global default"
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:border-indigo-300 focus:ring-1 focus:ring-indigo-200 outline-none" />
              <p className="text-[10px] text-slate-400 mt-1">Leave 0 or empty to use the global default from settings.</p>
            </div>
          )}

          {/* Address */}
          <div>
            <label className="text-xs font-bold text-slate-600 block mb-1.5">Address</label>
            <input value={form.address} onChange={e => upd('address', e.target.value)}
              placeholder="e.g. West Bay, Doha"
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:border-indigo-300 focus:ring-1 focus:ring-indigo-200 outline-none" />
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-bold text-slate-600 block mb-1.5">Description</label>
            <textarea value={form.description} onChange={e => upd('description', e.target.value)}
              placeholder="Notes about this location…"
              rows={3}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:border-indigo-300 focus:ring-1 focus:ring-indigo-200 outline-none resize-none" />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg font-medium">Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {isEdit ? 'Update' : 'Create'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════
// 4. TARGET MANAGEMENT TAB — v2 Financial Workspace
// ═══════════════════════════════════════════════════════════

/* Tiny SVG sparkline renderer — no external dependency */
function Sparkline({ data, width = 80, height = 24, color = '#6366f1' }: { data: number[]; width?: number; height?: number; color?: string }) {
  if (data.length < 2) return <div style={{ width, height }} className="bg-slate-50 rounded" />;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * (height - 4) - 2}`).join(' ');
  return (
    <svg width={width} height={height} className="shrink-0">
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* Weekend weighting: Fri/Sat get 40% more, rest adjusted to keep total intact */
function computeWeekendWeighted(monthlyTarget: number, year: number, month: number): { weekday: number; weekend: number } {
  const daysInMonth = new Date(year, month, 0).getDate();
  let weekendDays = 0;
  for (let d = 1; d <= daysInMonth; d++) {
    const dow = new Date(year, month - 1, d).getDay();
    if (dow === 5 || dow === 6) weekendDays++; // Fri=5, Sat=6
  }
  const weekdayDays = daysInMonth - weekendDays;
  // weekend_daily = weekday_daily * 1.4
  // weekdayDays * wd + weekendDays * 1.4 * wd = monthly
  const wd = monthlyTarget / (weekdayDays + weekendDays * 1.4);
  return { weekday: Math.round(wd), weekend: Math.round(wd * 1.4) };
}

function TargetManagementTab({ user }: { user: User | null }) {
  const isAdmin = hasTabVariant(user, 'market-map', 'target-management', 'admin');
  const toast = useToast();
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [periodMode, setPeriodMode] = useState<'standard' | 'custom'>('standard');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [locations, setLocations] = useState<MapLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [targets, setTargets] = useState<Record<string, { monthly: string; daily: string; calcRule: string }>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [locked, setLocked] = useState(false);
  const [lockLoading, setLockLoading] = useState(false);
  const [activeView, setActiveView] = useState<'targets' | 'vault'>('targets');
  const [snapshots, setSnapshots] = useState<any[]>([]);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [dailyHistory, setDailyHistory] = useState<Record<string, number[]>>({});
  const [dailyHistoryRaw, setDailyHistoryRaw] = useState<DailyHistoryEntry[]>([]);
  const [collapsedBrands, setCollapsedBrands] = useState<Set<string>>(new Set());
  const [brandFilter, setBrandFilter] = useState<string>('');
  const [forecastLoading, setForecastLoading] = useState<string | null>(null);
  const [forecastData, setForecastData] = useState<{ locationId: string; locationName: string; forecast: any; salesData: any[]; monthlyTarget: number; dailyTarget: number; daysElapsed: number; daysRemaining: number } | null>(null);

  // Computed period info
  const [periodYear, periodMonth] = month.split('-').map(Number);
  const daysInMonth = new Date(periodYear, periodMonth, 0).getDate();
  const customDays = useMemo(() => {
    if (periodMode !== 'custom' || !customFrom || !customTo) return daysInMonth;
    const from = new Date(customFrom);
    const to = new Date(customTo);
    return Math.max(1, Math.round((to.getTime() - from.getTime()) / 86400000) + 1);
  }, [periodMode, customFrom, customTo, daysInMonth]);
  const activeDays = periodMode === 'custom' ? customDays : daysInMonth;

  const loadData = useCallback(async () => {
    if (!isRemoteMode()) { setLoading(false); return; }
    setError(false);
    try {
      const [locRes, tgtRes, lockRes, histRes] = await Promise.all([
        apiFetch('/api/map/sales/summary'),
        apiFetch(`/api/map/targets?month=${month}`),
        apiFetch(`/api/map/targets/lock-status?month=${month}`),
        apiFetch(`/api/map/sales/daily-history?month=${month}`),
      ]);
      const locs: MapLocation[] = ((locRes as any).data ?? []).filter((l: MapLocation) => l.type === 'INTERNAL');
      const tgts: any[] = (tgtRes as any).data ?? [];
      setLocations(locs);
      setLocked(!!(lockRes as any).data?.locked);

      // Build daily history map: locationId -> [sales_day1, sales_day2, ...]
      const hist: Record<string, number[]> = {};
      const rawHist: any[] = (histRes as any).data ?? [];
      rawHist.forEach((r: any) => {
        if (!hist[r.location_id]) hist[r.location_id] = [];
        hist[r.location_id].push(Number(r.sales_amount || 0));
      });
      setDailyHistory(hist);
      setDailyHistoryRaw(rawHist.map((r: any) => ({ location_id: r.location_id, entry_date: r.entry_date, sales_amount: Number(r.sales_amount || 0) })));

      const tMap: Record<string, { monthly: string; daily: string; calcRule: string }> = {};
      locs.forEach(l => {
        const existing = tgts.find((t: any) => t.location_id === l.id);
        tMap[l.id] = {
          monthly: existing ? String(existing.monthly_target) : String(l.monthly_target || ''),
          daily: existing ? String(existing.daily_override || '') : String(l.daily_target || ''),
          calcRule: existing?.calc_rule || 'fixed',
        };
      });
      setTargets(tMap);
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadSnapshots = useCallback(async () => {
    setSnapshotLoading(true);
    try {
      const res = await apiFetch('/api/map/performance/snapshots?months=12');
      setSnapshots((res as any).data ?? []);
    } catch { /* ignore */ }
    finally { setSnapshotLoading(false); }
  }, []);

  useEffect(() => { if (activeView === 'vault') loadSnapshots(); }, [activeView, loadSnapshots]);

  const handleSaveTarget = async (locId: string) => {
    if (locked) return;
    const t = targets[locId];
    if (!t) return;
    setSaving(locId);
    try {
      await apiFetch('/api/map/targets', { method: 'PUT', body: JSON.stringify({
        locationId: locId, month,
        monthlyTarget: parseFloat(t.monthly) || 0,
        dailyOverride: parseFloat(t.daily) || 0,
        calcRule: t.calcRule || 'fixed',
      })});
      toast.success('Saved', 'Target updated.');
    } catch (e: any) { toast.error('Failed', e.message || 'Save failed.'); }
    finally { setSaving(null); }
  };

  const handleBulkSave = async () => {
    if (locked) return;
    const items = Object.entries(targets).map(([locId, t]) => ({
      locationId: locId,
      monthlyTarget: parseFloat(t.monthly) || 0,
      dailyOverride: parseFloat(t.daily) || 0,
      calcRule: t.calcRule || 'fixed',
    }));
    if (items.length === 0) return;
    setBulkSaving(true);
    try {
      await apiFetch('/api/map/targets/bulk', { method: 'POST', body: JSON.stringify({ month, targets: items }) });
      toast.success('Bulk Saved', `${items.length} targets updated for ${month}.`);
    } catch (e: any) { toast.error('Failed', e.message || 'Bulk save failed.'); }
    finally { setBulkSaving(false); }
  };

  const handleToggleLock = async () => {
    setLockLoading(true);
    try {
      if (locked) {
        await apiFetch('/api/map/targets/unlock', { method: 'POST', body: JSON.stringify({ month }) });
        setLocked(false);
        toast.success('Unlocked', 'Targets unlocked for editing.');
      } else {
        await apiFetch('/api/map/targets/lock', { method: 'POST', body: JSON.stringify({ month }) });
        setLocked(true);
        toast.success('Locked', 'Targets locked — inputs are now read-only.');
      }
    } catch (e: any) { toast.error('Failed', e.message); }
    finally { setLockLoading(false); }
  };

  const handleGenerateSnapshot = async () => {
    try {
      await apiFetch('/api/map/performance/snapshot', { method: 'POST', body: JSON.stringify({ month }) });
      toast.success('Snapshot Created', `Performance data archived for ${month}.`);
      loadSnapshots();
    } catch (e: any) { toast.error('Failed', e.message); }
  };

  const handleImportTargets = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files?.[0];
    if (!file || locked) return;
    try {
      const XLSX = await import('xlsx-js-style');
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<Record<string, any>>(ws);
      if (rows.length === 0) { toast.warning('Empty', 'No data in file.'); return; }
      const nameToId: Record<string, string> = {};
      locations.forEach(l => { nameToId[l.name.toLowerCase().trim()] = l.id; });
      const items = rows.map(r => {
        const name = String(r['Branch'] || r['Name'] || r['name'] || '').toLowerCase().trim();
        const id = nameToId[name];
        if (!id) return null;
        return { locationId: id, monthlyTarget: Number(r['Monthly Target'] || r['monthly_target'] || 0), dailyOverride: Number(r['Daily Override'] || r['daily_override'] || 0) };
      }).filter(Boolean);
      if (items.length === 0) { toast.warning('No Matches', 'No branch names matched.'); return; }
      await apiFetch('/api/map/targets/bulk', { method: 'POST', body: JSON.stringify({ month, targets: items }) });
      toast.success('Imported', `${items.length} targets imported for ${month}.`);
      loadData();
    } catch (e: any) { toast.error('Import Failed', e.message || 'Parse error.'); }
    finally { evt.target.value = ''; }
  };

  const handleExportTemplate = async () => {
    const XLSX = await import('xlsx-js-style');
    const headerStyle = { fill: { fgColor: { rgb: 'FF1e293b' } }, font: { bold: true, color: { rgb: 'FFFFFFFF' } }, alignment: { horizontal: 'center' as const } };

    // Sheet 1: Current Targets
    const aoa = [
      ['Branch', 'Brand', 'Monthly Target', 'Daily Override', 'Calc Rule', 'Projected Month-End'],
      ...locations.map(l => {
        const t = targets[l.id];
        const hist = dailyHistory[l.id] || [];
        const avgDaily = hist.length > 0 ? hist.reduce((a, b) => a + b, 0) / hist.length : 0;
        const projected = Math.round(avgDaily * daysInMonth);
        return [l.name, l.brand_name || '', t?.monthly || '', t?.daily || '', t?.calcRule || 'fixed', projected];
      }),
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    for (let ci = 0; ci < 6; ci++) {
      const cell = ws[XLSX.utils.encode_cell({ r: 0, c: ci })];
      if (cell) cell.s = headerStyle;
    }
    ws['!cols'] = [{ wch: 30 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 18 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Targets');

    // Sheet 2: Historical Trends (last 3 months of snapshots)
    if (snapshots.length === 0) {
      try {
        const res = await apiFetch('/api/map/performance/snapshots?months=3');
        const snaps: any[] = (res as any).data ?? [];
        if (snaps.length > 0) {
          const histAoa: any[][] = [['Branch', 'Brand', 'Month', 'Target', 'Actual', 'Variance', 'Achievement %']];
          snaps.forEach((s: any) => histAoa.push([s.location_name, s.brand_name, s.month, s.monthly_target, s.total_actual, s.variance, s.achievement_pct]));
          const ws2 = XLSX.utils.aoa_to_sheet(histAoa);
          for (let ci = 0; ci < 7; ci++) {
            const cell = ws2[XLSX.utils.encode_cell({ r: 0, c: ci })];
            if (cell) cell.s = headerStyle;
          }
          ws2['!cols'] = [{ wch: 30 }, { wch: 16 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
          XLSX.utils.book_append_sheet(wb, ws2, 'Historical Trends');
        }
      } catch { /* skip historical */ }
    } else {
      const histAoa: any[][] = [['Branch', 'Brand', 'Month', 'Target', 'Actual', 'Variance', 'Achievement %']];
      snapshots.forEach((s: any) => histAoa.push([s.location_name, s.brand_name, s.month, s.monthly_target, s.total_actual, s.variance, s.achievement_pct]));
      const ws2 = XLSX.utils.aoa_to_sheet(histAoa);
      for (let ci = 0; ci < 7; ci++) {
        const cell = ws2[XLSX.utils.encode_cell({ r: 0, c: ci })];
        if (cell) cell.s = headerStyle;
      }
      ws2['!cols'] = [{ wch: 30 }, { wch: 16 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'Historical Trends');
    }

    // Sheet 3: BI Summary
    appendBISummarySheet(XLSX, wb);

    XLSX.writeFile(wb, `Targets_${month}.xlsx`);
  };

  // AI Forecast handler
  const handleForecast = async (locationId: string, locationName: string) => {
    setForecastLoading(locationId);
    try {
      const res = await apiFetch<any>('/api/map/forecast', {
        method: 'POST',
        body: JSON.stringify({ location_id: locationId }),
      });
      const data = res.data ?? res;
      setForecastData({
        locationId,
        locationName,
        forecast: data.forecast,
        salesData: data.salesData || [],
        monthlyTarget: data.monthlyTarget || 0,
        dailyTarget: data.dailyTarget || 0,
        daysElapsed: data.daysElapsed || 0,
        daysRemaining: data.daysRemaining || 0,
      });
    } catch (e: any) {
      toast.error('Forecast Failed', e.message || 'Could not generate forecast.');
    } finally {
      setForecastLoading(null);
    }
  };

  // BI Summary sheet helper (used by both target export and analytics export)
  const appendBISummarySheet = (XLSX: any, wb: any) => {
    const biAoa: any[][] = [
      ['BI Summary — ' + month],
      [],
      ['Brand', 'Branches', 'Total Target', 'Total Actual', 'Achievement %', 'Gap (QAR)'],
      ...bi.brandSummaries.map(bs => [bs.brandName, bs.branchCount, bs.totalTarget, bs.totalActual, Math.round(bs.achievementPct * 10) / 10, bs.gap]),
      [],
      ['ORGANIZATION TOTAL', locations.length, bi.orgTotals.totalTarget, bi.orgTotals.totalActual, Math.round(bi.orgTotals.achievementPct * 10) / 10, bi.orgTotals.gap],
      [],
      ['Projection'],
      ['Projected Month-End', bi.projection.projectedTotal],
      ['Projected Achievement', Math.round(bi.projection.projectedPct * 10) / 10 + '%'],
      ['Expected Gap', bi.projection.projectedGap],
      ['Days Elapsed', bi.projection.daysElapsed + ' / ' + bi.projection.daysInMonth],
    ];
    const wsBI = XLSX.utils.aoa_to_sheet(biAoa);
    wsBI['!cols'] = [{ wch: 24 }, { wch: 12 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 14 }];
    const hdrStyle = { fill: { fgColor: { rgb: 'FF1e293b' } }, font: { bold: true, color: { rgb: 'FFFFFFFF' } }, alignment: { horizontal: 'center' as const } };
    for (let ci = 0; ci < 6; ci++) {
      const cell = wsBI[XLSX.utils.encode_cell({ r: 2, c: ci })];
      if (cell) cell.s = hdrStyle;
    }
    XLSX.utils.book_append_sheet(wb, wsBI, 'BI Summary');
  };

  // Brand grouping — filtered by brandFilter
  const filteredLocations = useMemo(() => {
    if (!brandFilter) return locations;
    return locations.filter(l => (l.brand_id || '__none__') === brandFilter);
  }, [locations, brandFilter]);

  const brandGroups = useMemo(() => {
    const groups: Record<string, { brandName: string; brandLogo: string; locs: MapLocation[] }> = {};
    filteredLocations.forEach(loc => {
      const key = loc.brand_id || '__none__';
      if (!groups[key]) groups[key] = { brandName: loc.brand_name || 'Unassigned', brandLogo: loc.brand_logo_url || '', locs: [] };
      groups[key].locs.push(loc);
    });
    return Object.entries(groups).sort((a, b) => a[1].brandName.localeCompare(b[1].brandName));
  }, [filteredLocations]);

  // BI Hook (all locations — not filtered — for correct summaries)
  const bi = useMarketBI(locations, targets, dailyHistoryRaw, month);

  const vaultMonths = useMemo(() => {
    const monthSet = new Set<string>();
    snapshots.forEach(s => monthSet.add(s.month));
    return [...monthSet].sort().reverse();
  }, [snapshots]);

  const toggleBrandCollapse = (key: string) => {
    setCollapsedBrands(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <Loader2 size={28} className="animate-spin text-indigo-400" />
        <p className="text-sm text-slate-400 font-medium">Loading targets…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <AlertTriangle size={28} className="text-amber-400" />
        <p className="text-sm text-slate-500 font-medium">Failed to load targets.</p>
        <button onClick={() => { setLoading(true); loadData(); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition-colors">
          <RefreshCw size={13} /> Retry
        </button>
      </div>
    );
  }

  // ─── PERFORMANCE VAULT VIEW ───
  if (activeView === 'vault') {

    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Archive size={22} className="text-indigo-500" /> Performance Vault</h2>
            <p className="text-sm text-slate-500 mt-1">Permanent monthly performance archive — last 12 months.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleGenerateSnapshot}
              className="flex items-center gap-1.5 px-3 py-2 bg-amber-600 text-white rounded-lg text-xs font-bold hover:bg-amber-700 transition-colors">
              <Archive size={13} /> Generate Snapshot for {month}
            </button>
            <button onClick={() => setActiveView('targets')}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:border-indigo-300 transition-colors">
              <Target size={13} /> Back to Targets
            </button>
          </div>
        </div>

        {snapshotLoading ? (
          <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin text-indigo-400" /></div>
        ) : snapshots.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center">
            <Archive size={32} className="text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-400 font-medium">No snapshots yet. Generate one from the current month's data.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {vaultMonths.map(vm => {
              const monthSnaps = snapshots.filter(s => s.month === vm);
              const totalTarget = monthSnaps.reduce((s, x) => s + Number(x.monthly_target || 0), 0);
              const totalActual = monthSnaps.reduce((s, x) => s + Number(x.total_actual || 0), 0);
              const overallPct = totalTarget > 0 ? (totalActual / totalTarget) * 100 : 0;
              return (
                <div key={vm} className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                  <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Calendar size={14} className="text-slate-400" />
                      <span className="text-sm font-bold text-slate-800">{vm}</span>
                      <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-black uppercase',
                        overallPct >= 95 ? 'bg-emerald-100 text-emerald-700' : overallPct >= 80 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700')}>
                        {overallPct >= 95 ? '🟢 SUCCESS' : overallPct >= 80 ? '🟡 PARTIAL' : '🔴 MISSED'} — {overallPct.toFixed(1)}%
                      </span>
                    </div>
                    <div className="text-xs text-slate-500">
                      Target: <span className="font-bold text-slate-700">{formatCurrency(totalTarget)}</span> · Actual: <span className="font-bold text-slate-700">{formatCurrency(totalActual)}</span>
                    </div>
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-slate-400 border-b border-slate-100">
                        <th className="text-left px-4 py-2 font-bold">Branch</th>
                        <th className="text-right px-4 py-2 font-bold">Target</th>
                        <th className="text-right px-4 py-2 font-bold">Actual</th>
                        <th className="text-right px-4 py-2 font-bold">Variance</th>
                        <th className="text-center px-4 py-2 font-bold">Achievement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthSnaps.map((s: any) => {
                        const pct = Number(s.achievement_pct || 0);
                        const variance = Number(s.variance || 0);
                        return (
                          <tr key={s.id} className="border-t border-slate-50 hover:bg-slate-50/50">
                            <td className="px-4 py-2">
                              <p className="font-semibold text-slate-800">{s.location_name}</p>
                              <p className="text-[10px] text-slate-400">{s.brand_name}</p>
                            </td>
                            <td className="px-4 py-2 text-right font-medium text-slate-600">{formatCurrency(s.monthly_target)}</td>
                            <td className="px-4 py-2 text-right font-medium text-slate-600">{formatCurrency(s.total_actual)}</td>
                            <td className={cn('px-4 py-2 text-right font-bold', variance >= 0 ? 'text-emerald-600' : 'text-red-500')}>
                              {variance >= 0 ? '+' : ''}{formatCurrency(variance)}
                            </td>
                            <td className="px-4 py-2 text-center">
                              <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-black',
                                pct >= 95 ? 'bg-emerald-100 text-emerald-700' : pct >= 80 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700')}>
                                {pct.toFixed(1)}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ─── TARGETS VIEW (main) ───
  return (
    <div className="space-y-4">
      {/* Header Bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            Target Management
            {locked && <span className="flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-600 rounded-full text-[10px] font-black uppercase"><Lock size={10} /> LOCKED</span>}
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">Financial workspace — set, track, and project sales targets by brand.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Period Controls */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
            <button onClick={() => setPeriodMode('standard')}
              className={cn('px-2.5 py-1.5 rounded-md text-[10px] font-bold transition-all', periodMode === 'standard' ? 'bg-white shadow text-slate-800' : 'text-slate-500')}>
              Standard
            </button>
            <button onClick={() => setPeriodMode('custom')}
              className={cn('px-2.5 py-1.5 rounded-md text-[10px] font-bold transition-all', periodMode === 'custom' ? 'bg-white shadow text-slate-800' : 'text-slate-500')}>
              Custom
            </button>
          </div>
          {periodMode === 'standard' ? (
            <input type="month" value={month} onChange={e => setMonth(e.target.value)}
              className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:border-indigo-300 outline-none" />
          ) : (
            <div className="flex items-center gap-1">
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                className="px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:border-indigo-300 outline-none" />
              <span className="text-xs text-slate-400">→</span>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                className="px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:border-indigo-300 outline-none" />
              <span className="text-[10px] text-slate-400 font-bold">{customDays}d</span>
            </div>
          )}
          <button onClick={handleExportTemplate}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-[11px] font-bold text-slate-600 hover:border-indigo-300 transition-colors">
            <Download size={12} /> Export
          </button>
          <label className={cn('flex items-center gap-1 px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-[11px] font-bold text-slate-600 hover:border-indigo-300 transition-colors', locked ? 'opacity-50 pointer-events-none' : 'cursor-pointer')}>
            <Upload size={12} /> Import
            <input type="file" accept=".xlsx,.xls" onChange={handleImportTargets} className="hidden" disabled={locked} />
          </label>
          <button onClick={() => setActiveView('vault')}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-[11px] font-bold text-slate-600 hover:border-indigo-300 transition-colors">
            <Archive size={12} /> Vault
          </button>
          {isAdmin && (
            <button onClick={handleToggleLock} disabled={lockLoading}
              className={cn('flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-colors',
                locked ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-slate-800 text-white hover:bg-slate-900')}>
              {lockLoading ? <Loader2 size={12} className="animate-spin" /> : locked ? <Unlock size={12} /> : <Lock size={12} />}
              {locked ? 'Unlock' : 'Lock'}
            </button>
          )}
          <button onClick={handleBulkSave} disabled={bulkSaving || locked}
            className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-[11px] font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50">
            {bulkSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save All
          </button>
        </div>
      </div>

      {/* Brand Summary Cards */}
      {bi.brandSummaries.length > 1 && (
        <div className="flex gap-3 overflow-x-auto pb-1">
          {/* All Brands card */}
          <button onClick={() => setBrandFilter('')}
            className={cn('shrink-0 min-w-[200px] bg-white border rounded-2xl p-3 text-left transition-all hover:shadow-md',
              !brandFilter ? 'border-indigo-400 ring-2 ring-indigo-100 shadow-sm' : 'border-slate-200')}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0"><Globe size={13} className="text-slate-500" /></div>
              <span className="text-xs font-bold text-slate-800">All Brands</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <p className="text-[9px] text-slate-400 font-bold uppercase">Target</p>
                <p className="text-xs font-black text-slate-700">{formatCurrency(bi.orgTotals.totalTarget)}</p>
              </div>
              <div>
                <p className="text-[9px] text-slate-400 font-bold uppercase">Achieve</p>
                <p className={cn('text-xs font-black', bi.orgTotals.achievementPct >= 95 ? 'text-emerald-600' : bi.orgTotals.achievementPct >= 80 ? 'text-amber-600' : 'text-red-500')}>
                  {bi.orgTotals.achievementPct.toFixed(1)}%
                </p>
              </div>
              <div>
                <p className="text-[9px] text-slate-400 font-bold uppercase">Gap</p>
                <p className={cn('text-xs font-black', bi.orgTotals.gap >= 0 ? 'text-emerald-600' : 'text-red-500')}>
                  {bi.orgTotals.gap >= 0 ? '+' : ''}{formatCurrency(bi.orgTotals.gap)}
                </p>
              </div>
            </div>
          </button>
          {bi.brandSummaries.map(bs => {
            const logoUrl = getMediaUrl(bs.brandLogo);
            return (
              <button key={bs.brandId} onClick={() => setBrandFilter(prev => prev === bs.brandId ? '' : bs.brandId)}
                className={cn('shrink-0 min-w-[200px] bg-white border rounded-2xl p-3 text-left transition-all hover:shadow-md',
                  brandFilter === bs.brandId ? 'border-indigo-400 ring-2 ring-indigo-100 shadow-sm' : 'border-slate-200')}>
                <div className="flex items-center gap-2 mb-2">
                  {logoUrl ? (
                    <img src={logoUrl} alt="" className="w-7 h-7 rounded-full object-cover border border-slate-200 shrink-0" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center shrink-0"><Building2 size={13} className="text-indigo-500" /></div>
                  )}
                  <span className="text-xs font-bold text-slate-800 truncate">{bs.brandName}</span>
                  <span className="text-[9px] text-slate-400 font-bold ml-auto">{bs.branchCount}</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <p className="text-[9px] text-slate-400 font-bold uppercase">Target</p>
                    <p className="text-xs font-black text-slate-700">{formatCurrency(bs.totalTarget)}</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-slate-400 font-bold uppercase">Achieve</p>
                    <p className={cn('text-xs font-black', bs.achievementPct >= 95 ? 'text-emerald-600' : bs.achievementPct >= 80 ? 'text-amber-600' : 'text-red-500')}>
                      {bs.achievementPct.toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-[9px] text-slate-400 font-bold uppercase">Gap</p>
                    <p className={cn('text-xs font-black', bs.gap >= 0 ? 'text-emerald-600' : 'text-red-500')}>
                      {bs.gap >= 0 ? '+' : ''}{formatCurrency(bs.gap)}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Brand-Grouped Target Table */}
      <div className="space-y-3">
        {brandGroups.map(([brandKey, group]) => {
          const isCollapsed = collapsedBrands.has(brandKey);
          const brandLogoUrl = getMediaUrl(group.brandLogo);
          const brandTotal = group.locs.reduce((s, l) => s + (Number(targets[l.id]?.monthly) || 0), 0);
          const brandActual = group.locs.reduce((s, l) => s + (l.current_sales ?? 0), 0);

          return (
            <div key={brandKey} className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
              {/* Brand Header */}
              <button onClick={() => toggleBrandCollapse(brandKey)}
                className="w-full flex items-center gap-3 px-4 py-2.5 bg-gradient-to-r from-slate-50 to-white hover:from-slate-100 transition-colors text-left">
                {isCollapsed ? <ChevronRight size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                {brandLogoUrl ? (
                  <img src={brandLogoUrl} alt="" className="w-6 h-6 rounded-full object-cover border border-slate-200 shrink-0" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                    <Building2 size={11} className="text-indigo-500" />
                  </div>
                )}
                <span className="text-sm font-bold text-slate-800 flex-1">{group.brandName}</span>
                <span className="text-[10px] text-slate-400 font-bold">{group.locs.length} branches</span>
                <span className="text-[10px] font-bold text-slate-500 ml-4">Target: {formatCurrency(brandTotal)}</span>
                <span className="text-[10px] font-bold text-indigo-500 ml-2">Actual: {formatCurrency(brandActual)}</span>
              </button>

              {/* Branches Table */}
              {!isCollapsed && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50/60 text-slate-400 border-t border-slate-100">
                        <th className="text-left pl-12 pr-3 py-2 font-bold w-[20%]">Branch</th>
                        <th className="text-center px-2 py-2 font-bold w-[13%]">Monthly Target</th>
                        <th className="text-center px-2 py-2 font-bold w-[11%]">Daily Target</th>
                        <th className="text-center px-2 py-2 font-bold w-[9%]">Auto Daily</th>
                        <th className="text-center px-2 py-2 font-bold w-[9%]">Calc Rule</th>
                        <th className="text-center px-2 py-2 font-bold w-[7%]">Trend</th>
                        <th className="text-center px-2 py-2 font-bold w-[13%]">Projected M/E</th>
                        <th className="text-center px-2 py-2 font-bold w-[10%]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.locs.map(loc => {
                        const t = targets[loc.id] ?? { monthly: '', daily: '', calcRule: 'fixed' };
                        const monthlyNum = Number(t.monthly) || 0;
                        const hist = dailyHistory[loc.id] || [];
                        const avgDaily = hist.length > 0 ? hist.reduce((a, b) => a + b, 0) / hist.length : 0;
                        const projectedMonthEnd = Math.round(avgDaily * daysInMonth);
                        const projectedGap = monthlyNum > 0 ? projectedMonthEnd - monthlyNum : 0;

                        // Auto daily calc based on rule
                        let autoDaily = 0;
                        if (monthlyNum > 0) {
                          if (t.calcRule === 'weighted') {
                            const w = computeWeekendWeighted(monthlyNum, periodYear, periodMonth);
                            autoDaily = w.weekday; // show weekday rate; tooltip shows weekend
                          } else {
                            autoDaily = Math.round(monthlyNum / activeDays);
                          }
                        }

                        const sparkColor = projectedGap >= 0 ? '#10b981' : '#ef4444';

                        return (
                          <tr key={loc.id} className="border-t border-slate-50 hover:bg-slate-50/50 group">
                            <td className="pl-12 pr-3 py-1.5">
                              <p className="font-semibold text-slate-800 text-xs leading-tight">{loc.name}</p>
                            </td>
                            <td className="px-2 py-1.5 text-center">
                              <input type="number" value={t.monthly} disabled={locked}
                                onChange={e => setTargets(prev => ({ ...prev, [loc.id]: { ...prev[loc.id], monthly: e.target.value } }))}
                                placeholder="150,000"
                                className={cn('w-full max-w-[120px] mx-auto px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs text-center focus:border-indigo-300 focus:ring-1 focus:ring-indigo-200 outline-none', locked && 'opacity-60 cursor-not-allowed')} />
                            </td>
                            <td className="px-2 py-1.5 text-center">
                              <input type="number" value={t.daily} disabled={locked}
                                onChange={e => setTargets(prev => ({ ...prev, [loc.id]: { ...prev[loc.id], daily: e.target.value } }))}
                                placeholder={autoDaily ? String(autoDaily) : '—'}
                                className={cn('w-full max-w-[100px] mx-auto px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs text-center focus:border-indigo-300 focus:ring-1 focus:ring-indigo-200 outline-none', locked && 'opacity-60 cursor-not-allowed')} />
                            </td>
                            <td className="px-2 py-1.5 text-center">
                              {autoDaily > 0 ? (
                                <div className="text-slate-500 font-medium" title={t.calcRule === 'weighted' ? `Weekday: ${formatCurrency(autoDaily)} · Weekend: ${formatCurrency(Math.round(autoDaily * 1.4))}` : ''}>
                                  {formatCurrency(autoDaily)}
                                  {t.calcRule === 'weighted' && <span className="text-[8px] text-amber-500 ml-0.5">⚡</span>}
                                </div>
                              ) : <span className="text-slate-300">—</span>}
                            </td>
                            <td className="px-2 py-1.5 text-center">
                              <select value={t.calcRule || 'fixed'} disabled={locked}
                                onChange={e => setTargets(prev => ({ ...prev, [loc.id]: { ...prev[loc.id], calcRule: e.target.value } }))}
                                className={cn('px-1.5 py-1 bg-slate-50 border border-slate-200 rounded-md text-[10px] focus:border-indigo-300 outline-none', locked && 'opacity-60 cursor-not-allowed')}>
                                <option value="fixed">Fixed</option>
                                <option value="weighted">Weighted ⚡</option>
                              </select>
                            </td>
                            <td className="px-2 py-1.5">
                              <div className="flex justify-center">
                                <Sparkline data={hist} width={64} height={20} color={sparkColor} />
                              </div>
                            </td>
                            <td className="px-2 py-1.5 text-center">
                              {hist.length > 0 && monthlyNum > 0 ? (
                                <div>
                                  <p className="text-[10px] font-bold text-slate-600">{formatCurrency(projectedMonthEnd)}</p>
                                  <p className={cn('text-[9px] font-black', projectedGap >= 0 ? 'text-emerald-600' : 'text-red-500')}>
                                    {projectedGap >= 0 ? '+' : ''}{formatCurrency(projectedGap)}
                                  </p>
                                </div>
                              ) : <span className="text-[10px] text-slate-300">—</span>}
                            </td>
                            <td className="px-2 py-1.5 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {!locked && (
                                  <button onClick={() => handleSaveTarget(loc.id)} disabled={saving === loc.id}
                                    className="p-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50">
                                    {saving === loc.id ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                                  </button>
                                )}
                                <button onClick={() => handleForecast(loc.id, loc.name)} disabled={forecastLoading === loc.id}
                                  title="AI Sales Forecast"
                                  className="p-1 bg-violet-600 text-white rounded-md hover:bg-violet-700 transition-colors disabled:opacity-50">
                                  {forecastLoading === loc.id ? <Loader2 size={10} className="animate-spin" /> : <Bot size={10} />}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
        {filteredLocations.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center text-sm text-slate-400">
            {brandFilter ? 'No branches for this brand. Click the brand card again to clear filter.' : 'No internal branches found. Add locations first.'}
          </div>
        )}
      </div>

      {/* AI Forecast Slide-Out Panel */}
      <AnimatePresence>
        {forecastData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex justify-end bg-black/30 backdrop-blur-sm"
            onClick={() => setForecastData(null)}
          >
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full max-w-md bg-white h-full shadow-2xl overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-5 z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Bot size={16} className="text-white/80" />
                      <span className="text-[10px] font-bold uppercase tracking-widest text-white/60">AI Forecast</span>
                    </div>
                    <h3 className="text-white font-bold text-lg">{forecastData.locationName}</h3>
                  </div>
                  <button onClick={() => setForecastData(null)} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                    <X size={18} className="text-white" />
                  </button>
                </div>
                <p className="text-white/50 text-[10px] mt-1 flex items-center gap-1">
                  <Shield size={10} /> Blind Math — no business names sent to AI
                </p>
              </div>

              {/* Forecast Content */}
              <div className="p-6 space-y-5">
                {/* Risk Badge + Trend */}
                <div className="flex items-center gap-3">
                  <span className={cn('px-3 py-1 rounded-full text-[10px] font-black uppercase',
                    forecastData.forecast.risk_level === 'on_track' ? 'bg-emerald-100 text-emerald-700' :
                    forecastData.forecast.risk_level === 'at_risk' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700')}>
                    {forecastData.forecast.risk_level === 'on_track' ? '✓ On Track' :
                     forecastData.forecast.risk_level === 'at_risk' ? '⚠ At Risk' : '✘ Critical'}
                  </span>
                  <span className={cn('flex items-center gap-1 text-xs font-bold',
                    forecastData.forecast.trend === 'up' ? 'text-emerald-600' : forecastData.forecast.trend === 'down' ? 'text-red-500' : 'text-slate-500')}>
                    {forecastData.forecast.trend === 'up' ? <TrendingUp size={14} /> : forecastData.forecast.trend === 'down' ? <TrendingDown size={14} /> : <Activity size={14} />}
                    {forecastData.forecast.trend_pct > 0 ? '+' : ''}{forecastData.forecast.trend_pct?.toFixed(1)}%
                  </span>
                </div>

                {/* Key Metrics Grid */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-[9px] text-slate-400 font-bold uppercase">Projected Month-End</p>
                    <p className="text-lg font-black text-slate-800">{formatCurrency(forecastData.forecast.projected_month_end)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-[9px] text-slate-400 font-bold uppercase">Achievement Forecast</p>
                    <p className={cn('text-lg font-black', (forecastData.forecast.achievement_forecast_pct || 0) >= 95 ? 'text-emerald-600' : (forecastData.forecast.achievement_forecast_pct || 0) >= 80 ? 'text-amber-600' : 'text-red-500')}>
                      {forecastData.forecast.achievement_forecast_pct?.toFixed(1)}%
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-[9px] text-slate-400 font-bold uppercase">Daily Average</p>
                    <p className="text-lg font-black text-slate-800">{formatCurrency(forecastData.forecast.daily_average)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-[9px] text-slate-400 font-bold uppercase">Monthly Target</p>
                    <p className="text-lg font-black text-slate-800">{formatCurrency(forecastData.monthlyTarget)}</p>
                  </div>
                </div>

                {/* Best/Worst Days */}
                <div className="flex gap-3">
                  <div className="flex-1 bg-emerald-50 rounded-xl p-3 flex items-center gap-2">
                    <Star size={14} className="text-emerald-500" />
                    <div>
                      <p className="text-[9px] text-emerald-600/60 font-bold uppercase">Best Day</p>
                      <p className="text-sm font-bold text-emerald-700">{forecastData.forecast.best_day}</p>
                    </div>
                  </div>
                  <div className="flex-1 bg-red-50 rounded-xl p-3 flex items-center gap-2">
                    <AlertTriangle size={14} className="text-red-400" />
                    <div>
                      <p className="text-[9px] text-red-600/60 font-bold uppercase">Worst Day</p>
                      <p className="text-sm font-bold text-red-700">{forecastData.forecast.worst_day}</p>
                    </div>
                  </div>
                </div>

                {/* Weekly Pattern */}
                {forecastData.forecast.weekly_pattern && (
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-[9px] text-slate-400 font-bold uppercase mb-3">Weekly Pattern</p>
                    <div className="flex items-end justify-between gap-1 h-16">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => {
                        const val = forecastData.forecast.weekly_pattern[i] || 0;
                        const maxVal = Math.max(...(forecastData.forecast.weekly_pattern || [1]));
                        const pct = maxVal > 0 ? (val / maxVal) * 100 : 0;
                        return (
                          <div key={day} className="flex flex-col items-center gap-1 flex-1">
                            <span className="text-[8px] font-bold text-slate-500">{formatCurrency(val)}</span>
                            <div className="w-full bg-slate-200 rounded-t-md" style={{ height: `${Math.max(pct, 5)}%` }}>
                              <div className={cn('w-full h-full rounded-t-md', i >= 4 ? 'bg-amber-400' : 'bg-indigo-400')} />
                            </div>
                            <span className="text-[8px] font-bold text-slate-400">{day}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* AI Recommendation */}
                <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Bot size={14} className="text-violet-500" />
                    <span className="text-[10px] font-bold uppercase text-violet-600/60">AI Recommendation</span>
                  </div>
                  <p className="text-sm text-violet-800 leading-relaxed">{forecastData.forecast.recommendation}</p>
                </div>

                {/* Progress Bar */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] font-bold text-slate-500">Month Progress</span>
                    <span className="text-[10px] font-bold text-slate-500">{forecastData.daysElapsed} / {forecastData.daysElapsed + forecastData.daysRemaining} days</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-indigo-500 rounded-full transition-all"
                      style={{ width: `${((forecastData.daysElapsed) / (forecastData.daysElapsed + forecastData.daysRemaining)) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// 5. ANALYTICS DASHBOARD TAB
// ═══════════════════════════════════════════════════════════

/* Simple inline SVG bar chart */
function HorizontalBar({ value, max, label, color = '#6366f1' }: { value: number; max: number; label: string; color?: string }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] font-semibold text-zinc-300 w-28 truncate" title={label}>{label}</span>
      <div className="flex-1 h-6 bg-[#2D2D2D] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all flex items-center justify-end pr-2" style={{ width: `${pct}%`, backgroundColor: color, minWidth: pct > 0 ? '24px' : '0' }}>
          {pct > 8 && <span className="text-[9px] font-bold text-white">{value.toFixed(1)}%</span>}
        </div>
      </div>
      {pct <= 8 && <span className="text-[10px] font-bold text-zinc-400">{value.toFixed(1)}%</span>}
    </div>
  );
}

/* Inline SVG Line Chart for daily velocity */
function VelocityChart({ data, width = 600, height = 200 }: { data: { day: number; idealCumulative: number; actualCumulative: number }[]; width?: number; height?: number }) {
  if (data.length < 2) return <div className="flex items-center justify-center h-48 bg-[#1E1E1E] rounded-xl text-sm text-zinc-500">Not enough data to plot</div>;
  const maxVal = Math.max(...data.map(d => Math.max(d.idealCumulative, d.actualCumulative)), 1);
  const padX = 48;
  const padY = 20;
  const chartW = width - padX * 2;
  const chartH = height - padY * 2;
  const x = (i: number) => padX + (i / (data.length - 1)) * chartW;
  const yScale = (v: number) => padY + chartH - (v / maxVal) * chartH;

  const idealPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${yScale(d.idealCumulative)}`).join(' ');
  const actualPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${yScale(d.actualCumulative)}`).join(' ');

  // Y-axis labels
  const yLabels = [0, maxVal * 0.25, maxVal * 0.5, maxVal * 0.75, maxVal].map(v => ({ v, y: yScale(v) }));

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ maxHeight: height }}>
      {/* Grid lines */}
      {yLabels.map((yl, i) => (
        <g key={i}>
          <line x1={padX} x2={width - padX} y1={yl.y} y2={yl.y} stroke="#2D2D2D" strokeWidth={0.5} />
          <text x={padX - 6} y={yl.y + 3} textAnchor="end" fill="#71717a" fontSize={9} fontWeight={600}>{formatCurrency(Math.round(yl.v))}</text>
        </g>
      ))}
      {/* X-axis day labels */}
      {data.filter((_, i) => i % Math.max(1, Math.floor(data.length / 8)) === 0).map((d, i) => (
        <text key={i} x={x(data.indexOf(d))} y={height - 4} textAnchor="middle" fill="#71717a" fontSize={9} fontWeight={600}>Day {d.day}</text>
      ))}
      {/* Ideal line (dashed) */}
      <path d={idealPath} fill="none" stroke="#52525b" strokeWidth={1.5} strokeDasharray="4 3" />
      {/* Actual line */}
      <path d={actualPath} fill="none" stroke="#3b82f6" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
      {/* Legend */}
      <line x1={padX + 10} x2={padX + 30} y1={10} y2={10} stroke="#3b82f6" strokeWidth={2.5} />
      <text x={padX + 34} y={13} fill="#d4d4d8" fontSize={9} fontWeight={700}>Actual</text>
      <line x1={padX + 100} x2={padX + 120} y1={10} y2={10} stroke="#52525b" strokeWidth={1.5} strokeDasharray="4 3" />
      <text x={padX + 124} y={13} fill="#71717a" fontSize={9} fontWeight={700}>Ideal Run-Rate</text>
    </svg>
  );
}

function AnalyticsDashboardTab({ user }: { user: User | null }) {
  const isAdmin = hasTabVariant(user, 'market-map', 'analytics', 'admin');
  const toast = useToast();
  const now = new Date();
  const [month, setMonth] = usePersistentState('period', `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [brandFilters, setBrandFilters] = usePersistentState('brand', '');
  const [locations, setLocations] = useState<MapLocation[]>([]);
  const [targets, setTargets] = useState<Record<string, { monthly: string; daily: string; calcRule: string }>>({});
  const [dailyHistoryRaw, setDailyHistoryRaw] = useState<DailyHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadData = useCallback(async () => {
    if (!isRemoteMode()) { setLoading(false); return; }
    setError(false);
    try {
      const [locRes, tgtRes, histRes] = await Promise.all([
        apiFetch('/api/map/sales/summary'),
        apiFetch(`/api/map/targets?month=${month}`),
        apiFetch(`/api/map/sales/daily-history?month=${month}`),
      ]);
      const locs: MapLocation[] = ((locRes as any).data ?? []).filter((l: MapLocation) => l.type === 'INTERNAL');
      const tgts: any[] = (tgtRes as any).data ?? [];
      setLocations(locs);
      setDailyHistoryRaw(((histRes as any).data ?? []).map((r: any) => ({ location_id: r.location_id, entry_date: r.entry_date, sales_amount: Number(r.sales_amount || 0) })));

      const tMap: Record<string, { monthly: string; daily: string; calcRule: string }> = {};
      locs.forEach(l => {
        const existing = tgts.find((t: any) => t.location_id === l.id);
        tMap[l.id] = {
          monthly: existing ? String(existing.monthly_target) : String(l.monthly_target || ''),
          daily: existing ? String(existing.daily_override || '') : String(l.daily_target || ''),
          calcRule: existing?.calc_rule || 'fixed',
        };
      });
      setTargets(tMap);
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { loadData(); }, [loadData]);

  // Brand-gate: managers see only their assigned brands
  const filteredLocations = useMemo(() => {
    let locs = locations;
    // Manager brand restriction
    if (!isAdmin && user?.assignedBrands && user.assignedBrands.length > 0) {
      const allowed = new Set(user.assignedBrands);
      locs = locs.filter(l => allowed.has(l.brand_id));
    }
    // User-selected brand filter
    if (brandFilters) {
      const selected = new Set(brandFilters.split(',').filter(Boolean));
      if (selected.size > 0) locs = locs.filter(l => selected.has(l.brand_id || '__none__'));
    }
    return locs;
  }, [locations, brandFilters, isAdmin, user]);

  const filteredDailyHistory = useMemo(() => {
    const locIds = new Set(filteredLocations.map(l => l.id));
    return dailyHistoryRaw.filter(e => locIds.has(e.location_id));
  }, [dailyHistoryRaw, filteredLocations]);

  // BI calculations
  const bi = useMarketBI(filteredLocations, targets, filteredDailyHistory, month);

  // Brand list for filter
  const availableBrands = useMemo(() => {
    const map = new Map<string, { id: string; name: string; logo: string }>();
    locations.forEach(l => {
      const key = l.brand_id || '__none__';
      if (!map.has(key)) map.set(key, { id: key, name: l.brand_name || 'Unassigned', logo: l.brand_logo_url || '' });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [locations]);

  const toggleBrandFilter = (id: string) => {
    const current = new Set(brandFilters.split(',').filter(Boolean));
    if (current.has(id)) current.delete(id); else current.add(id);
    setBrandFilters(Array.from(current).join(','));
  };

  const handleExportAnalytics = async () => {
    const XLSX = await import('xlsx-js-style');
    const wb = XLSX.utils.book_new();
    const headerStyle = { fill: { fgColor: { rgb: 'FF1e293b' } }, font: { bold: true, color: { rgb: 'FFFFFFFF' } }, alignment: { horizontal: 'center' as const } };

    // Sheet 1: Brand Performance
    const brandAoa: any[][] = [['Brand', 'Branches', 'Total Target', 'Total Actual', 'Achievement %', 'Gap (QAR)']];
    bi.brandSummaries.forEach(bs => brandAoa.push([bs.brandName, bs.branchCount, bs.totalTarget, bs.totalActual, Math.round(bs.achievementPct * 10) / 10, bs.gap]));
    brandAoa.push([]);
    brandAoa.push(['TOTAL', filteredLocations.length, bi.orgTotals.totalTarget, bi.orgTotals.totalActual, Math.round(bi.orgTotals.achievementPct * 10) / 10, bi.orgTotals.gap]);
    const ws1 = XLSX.utils.aoa_to_sheet(brandAoa);
    for (let ci = 0; ci < 6; ci++) { const cell = ws1[XLSX.utils.encode_cell({ r: 0, c: ci })]; if (cell) cell.s = headerStyle; }
    ws1['!cols'] = [{ wch: 24 }, { wch: 10 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, ws1, 'Brand Performance');

    // Sheet 2: Branch Leaderboard
    const lbAoa: any[][] = [['Rank', 'Branch', 'Brand', 'Target', 'Actual', 'Achievement %', 'Gap']];
    [...bi.leaderboard.top, ...bi.leaderboard.bottom.filter(b => !bi.leaderboard.top.find(t => t.id === b.id))].forEach((b, i) => {
      lbAoa.push([i + 1, b.name, b.brandName, b.target, b.actual, Math.round(b.achievementPct * 10) / 10, b.gap]);
    });
    const ws2 = XLSX.utils.aoa_to_sheet(lbAoa);
    for (let ci = 0; ci < 7; ci++) { const cell = ws2[XLSX.utils.encode_cell({ r: 0, c: ci })]; if (cell) cell.s = headerStyle; }
    ws2['!cols'] = [{ wch: 6 }, { wch: 28 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, ws2, 'Leaderboard');

    // Sheet 3: Projection
    const projAoa: any[][] = [
      ['Projection Intelligence — ' + month],
      [],
      ['Metric', 'Value'],
      ['Total Target', bi.projection.totalTarget],
      ['Total Actual (to date)', bi.projection.totalActual],
      ['Projected Month-End', bi.projection.projectedTotal],
      ['Projected Achievement %', Math.round(bi.projection.projectedPct * 10) / 10],
      ['Expected Gap', bi.projection.projectedGap],
      ['Days Elapsed', bi.projection.daysElapsed + ' / ' + bi.projection.daysInMonth],
    ];
    const ws3 = XLSX.utils.aoa_to_sheet(projAoa);
    ws3['!cols'] = [{ wch: 28 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, ws3, 'Projection');

    XLSX.writeFile(wb, `Analytics_${month}.xlsx`);
    toast.success('Exported', 'Analytics report downloaded.');
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <Loader2 size={28} className="animate-spin text-blue-400" />
        <p className="text-sm text-zinc-400 font-medium">Loading analytics…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <AlertTriangle size={28} className="text-amber-400" />
        <p className="text-sm text-zinc-500 font-medium">Failed to load data.</p>
        <button onClick={() => { setLoading(true); loadData(); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 transition-colors">
          <RefreshCw size={13} /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-[#121212] rounded-2xl p-6 space-y-4">
      {/* Header + Global Filters */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-white flex items-center gap-2"><BarChart2 size={22} className="text-blue-400" /> Analytics Dashboard</h2>
          <p className="text-sm text-zinc-400 mt-0.5">Business Intelligence — real-time performance insights across all brands.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            className="px-3 py-1.5 bg-[#1E1E1E] border border-[#2D2D2D] rounded-lg text-xs text-zinc-200 focus:border-blue-500 outline-none" />
          <button onClick={handleExportAnalytics}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 text-white rounded-lg text-[11px] font-bold hover:bg-emerald-700 transition-colors">
            <Download size={12} /> Export Report
          </button>
        </div>
      </div>

      {/* Brand Filter Bar */}
      {availableBrands.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 text-xs text-zinc-400 font-bold"><Filter size={12} /> Brands:</div>
          <button onClick={() => setBrandFilters('')}
            className={cn('px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all',
              !brandFilters ? 'bg-blue-600 text-white border-blue-600' : 'bg-[#1E1E1E] text-zinc-400 border-[#2D2D2D] hover:border-blue-500')}>
            All
          </button>
          {availableBrands.map(b => {
            const isActive = brandFilters.split(',').includes(b.id);
            const logoUrl = getMediaUrl(b.logo);
            return (
              <button key={b.id} onClick={() => toggleBrandFilter(b.id)}
                className={cn('flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all',
                  isActive ? 'bg-blue-600 text-white border-blue-600' : 'bg-[#1E1E1E] text-zinc-300 border-[#2D2D2D] hover:border-blue-500')}>
                {logoUrl ? <img src={logoUrl} alt="" className="w-4 h-4 rounded-full object-cover" /> : null}
                {b.name}
              </button>
            );
          })}
        </div>
      )}

      {/* KPI Row — Organization Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-4 shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
          <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Total Target</p>
          <p className="text-xl font-black text-white mt-1">{formatCurrency(bi.orgTotals.totalTarget)}</p>
          <p className="text-[10px] text-zinc-500 mt-0.5">QAR · {month}</p>
        </div>
        <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-4 shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
          <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Total Actual</p>
          <p className="text-xl font-black text-blue-400 mt-1">{formatCurrency(bi.orgTotals.totalActual)}</p>
          <p className="text-[10px] text-zinc-500 mt-0.5">QAR · Live</p>
        </div>
        <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-4 shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
          <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Achievement</p>
          <p className={cn('text-xl font-black mt-1', bi.orgTotals.achievementPct >= 95 ? 'text-emerald-400' : bi.orgTotals.achievementPct >= 80 ? 'text-amber-400' : 'text-red-400')}>
            {bi.orgTotals.achievementPct.toFixed(1)}%
          </p>
          <p className="text-[10px] text-zinc-500 mt-0.5">{bi.orgTotals.achievementPct >= 95 ? '🟢 On Target' : bi.orgTotals.achievementPct >= 80 ? '🟡 Partial' : '🔴 Below Target'}</p>
        </div>
        <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-4 shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
          <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Gap to Goal</p>
          <p className={cn('text-xl font-black mt-1', bi.orgTotals.gap >= 0 ? 'text-emerald-400' : 'text-red-400')}>
            {bi.orgTotals.gap >= 0 ? '+' : ''}{formatCurrency(bi.orgTotals.gap)}
          </p>
          <p className="text-[10px] text-zinc-500 mt-0.5">QAR {bi.orgTotals.gap >= 0 ? 'surplus' : 'shortfall'}</p>
        </div>
      </div>

      {/* Two-column layout: Brand Bar Chart + Projection */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Performance by Brand — Bar Chart */}
        <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl shadow-[0_4px_24px_rgba(0,0,0,0.6)] overflow-hidden">
          <div className="px-5 py-3 bg-[#161616] border-b border-[#2D2D2D]">
            <p className="text-sm font-bold text-white flex items-center gap-2"><BarChart3 size={14} className="text-blue-400" /> Performance by Brand</p>
            <p className="text-[10px] text-zinc-500 mt-0.5">Achievement % comparison across brands</p>
          </div>
          <div className="p-5 space-y-2">
            {bi.brandSummaries.map(bs => (
              <HorizontalBar key={bs.brandId} value={bs.achievementPct} max={120} label={bs.brandName}
                color={bs.achievementPct >= 95 ? '#10b981' : bs.achievementPct >= 80 ? '#f59e0b' : '#ef4444'} />
            ))}
            {bi.brandSummaries.length === 0 && <p className="text-xs text-zinc-500 text-center py-4">No brand data available</p>}
          </div>
        </div>

        {/* AI-Style Projection Card */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 rounded-2xl shadow-sm overflow-hidden text-white">
          <div className="px-5 py-3 border-b border-slate-700 flex items-center gap-2">
            <Bot size={14} className="text-indigo-400" />
            <p className="text-sm font-bold">Projection Intelligence</p>
            <span className="text-[8px] font-black uppercase px-1.5 py-0.5 bg-indigo-500/30 text-indigo-300 rounded-full ml-auto">AI-SIMULATED</span>
          </div>
          <div className="p-5 space-y-4">
            <p className="text-sm leading-relaxed text-slate-300">
              Based on current trends, the organization is projected to hit{' '}
              <span className={cn('font-black', bi.projection.projectedPct >= 95 ? 'text-emerald-400' : bi.projection.projectedPct >= 80 ? 'text-amber-400' : 'text-red-400')}>
                {bi.projection.projectedPct.toFixed(1)}%
              </span>{' '}
              of the target.{' '}
              {bi.projection.projectedGap < 0 ? (
                <>A gap of <span className="font-black text-red-400">QAR {formatCurrency(Math.abs(bi.projection.projectedGap))}</span> is expected by month-end.</>
              ) : (
                <>A surplus of <span className="font-black text-emerald-400">QAR {formatCurrency(bi.projection.projectedGap)}</span> is projected by month-end.</>
              )}
            </p>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-[9px] text-slate-400 font-bold uppercase">Projected M/E</p>
                <p className="text-lg font-black text-white mt-0.5">{formatCurrency(bi.projection.projectedTotal)}</p>
              </div>
              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-[9px] text-slate-400 font-bold uppercase">Days Left</p>
                <p className="text-lg font-black text-white mt-0.5">{bi.projection.daysInMonth - bi.projection.daysElapsed}</p>
              </div>
              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-[9px] text-slate-400 font-bold uppercase">Avg Daily</p>
                <p className="text-lg font-black text-white mt-0.5">
                  {bi.projection.daysElapsed > 0 ? formatCurrency(Math.round(bi.projection.totalActual / bi.projection.daysElapsed)) : '—'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Sales Velocity — Line Chart */}
      <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl shadow-[0_4px_24px_rgba(0,0,0,0.6)] overflow-hidden">
        <div className="px-5 py-3 bg-[#161616] border-b border-[#2D2D2D] flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-white flex items-center gap-2"><Activity size={14} className="text-blue-400" /> Daily Sales Velocity</p>
            <p className="text-[10px] text-zinc-500 mt-0.5">Actual cumulative vs. ideal run-rate for {month}</p>
          </div>
          {bi.dailyVelocity.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-[10px] text-zinc-400">
                Day {bi.dailyVelocity[bi.dailyVelocity.length - 1]?.day}: <span className="font-bold text-blue-400">{formatCurrency(bi.dailyVelocity[bi.dailyVelocity.length - 1]?.actualCumulative ?? 0)}</span>
                {' '}vs ideal <span className="font-bold text-zinc-500">{formatCurrency(bi.dailyVelocity[bi.dailyVelocity.length - 1]?.idealCumulative ?? 0)}</span>
              </span>
            </div>
          )}
        </div>
        <div className="p-4">
          <VelocityChart data={bi.dailyVelocity} />
        </div>
      </div>

      {/* Leaderboard: Top + Bottom */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Performers */}
        <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl shadow-[0_4px_24px_rgba(0,0,0,0.6)] overflow-hidden">
          <div className="px-5 py-3 bg-[#161616] border-b border-[#2D2D2D]">
            <p className="text-sm font-bold text-white flex items-center gap-2"><Trophy size={14} className="text-amber-500" /> Top Performing Branches</p>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#252525] text-zinc-500 border-b border-[#333]">
                <th className="text-left px-4 py-2 font-bold">#</th>
                <th className="text-left px-4 py-2 font-bold">Branch</th>
                <th className="text-right px-4 py-2 font-bold">Achievement</th>
                <th className="text-right px-4 py-2 font-bold">Gap</th>
              </tr>
            </thead>
            <tbody>
              {bi.leaderboard.top.map((b, i) => (
                <tr key={b.id} className="border-b border-[#2A2A2A] hover:bg-[#2D2D2D]">
                  <td className="px-4 py-2 font-black text-blue-400">{i + 1}</td>
                  <td className="px-4 py-2">
                    <p className="font-semibold text-zinc-100">{b.name}</p>
                    <p className="text-[10px] text-zinc-500">{b.brandName}</p>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-black', b.achievementPct >= 95 ? 'bg-emerald-900/30 text-emerald-400' : b.achievementPct >= 80 ? 'bg-amber-900/30 text-amber-400' : 'bg-red-900/30 text-red-400')}>
                      {b.achievementPct.toFixed(1)}%
                    </span>
                  </td>
                  <td className={cn('px-4 py-2 text-right font-bold', b.gap >= 0 ? 'text-emerald-400' : 'text-red-400')}>
                    {b.gap >= 0 ? '+' : ''}{formatCurrency(b.gap)}
                  </td>
                </tr>
              ))}
              {bi.leaderboard.top.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-zinc-500">No data</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Under Target */}
        <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl shadow-[0_4px_24px_rgba(0,0,0,0.6)] overflow-hidden">
          <div className="px-5 py-3 bg-[#161616] border-b border-[#2D2D2D]">
            <p className="text-sm font-bold text-white flex items-center gap-2"><AlertTriangle size={14} className="text-red-500" /> Branches Under Target</p>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#252525] text-zinc-500 border-b border-[#333]">
                <th className="text-left px-4 py-2 font-bold">#</th>
                <th className="text-left px-4 py-2 font-bold">Branch</th>
                <th className="text-right px-4 py-2 font-bold">Achievement</th>
                <th className="text-right px-4 py-2 font-bold">Gap</th>
              </tr>
            </thead>
            <tbody>
              {bi.leaderboard.bottom.map((b, i) => (
                <tr key={b.id} className="border-b border-[#2A2A2A] hover:bg-[#2D2D2D]">
                  <td className="px-4 py-2 font-black text-red-400">{i + 1}</td>
                  <td className="px-4 py-2">
                    <p className="font-semibold text-zinc-100">{b.name}</p>
                    <p className="text-[10px] text-zinc-500">{b.brandName}</p>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-black', b.achievementPct >= 95 ? 'bg-emerald-900/30 text-emerald-400' : b.achievementPct >= 80 ? 'bg-amber-900/30 text-amber-400' : 'bg-red-900/30 text-red-400')}>
                      {b.achievementPct.toFixed(1)}%
                    </span>
                  </td>
                  <td className={cn('px-4 py-2 text-right font-bold', b.gap >= 0 ? 'text-emerald-400' : 'text-red-400')}>
                    {b.gap >= 0 ? '+' : ''}{formatCurrency(b.gap)}
                  </td>
                </tr>
              ))}
              {bi.leaderboard.bottom.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-zinc-500">No data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// 6. INTELLIGENCE ARCHIVE TAB
// ═══════════════════════════════════════════════════════════

interface ArchiveRow {
  id: string;
  org_id: string;
  log_date: string;
  brand_id: string | null;
  brand_name: string;
  total_sales: number;
  total_target: number;
  overall_ratio: number;
  green_count: number;
  amber_count: number;
  red_count: number;
  champion_count: number;
  branch_data: { id: string; name: string; brand?: string; sales: number; target: number }[];
  alert_feed: any[];
  created_at: number;
  created_by: string;
}

function IntelligenceArchiveTab({ user }: { user: User | null }) {
  const isAdmin = hasTabVariant(user, 'market-map', 'archive', 'admin');
  const toast = useToast();

  const [dates, setDates] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [brandFilter, setBrandFilter] = useState<string>('');
  const [rows, setRows] = useState<ArchiveRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closeDateInput, setCloseDateInput] = useState(() => {
    const d = new Date(); d.setDate(d.getDate());
    return d.toISOString().slice(0, 10);
  });

  // Load available dates
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/map/history/dates');
        const d = res.data ?? res ?? [];
        setDates(d);
        if (d.length > 0 && !selectedDate) setSelectedDate(d[0]);
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, []);

  // Load archive data for selected date
  useEffect(() => {
    if (!selectedDate) return;
    (async () => {
      setLoadingData(true);
      try {
        const url = `/api/map/history/${selectedDate}${brandFilter ? `?brand_id=${brandFilter}` : ''}`;
        const res = await apiFetch(url);
        setRows(res.data ?? res ?? []);
      } catch { setRows([]); }
      setLoadingData(false);
    })();
  }, [selectedDate, brandFilter]);

  const handleCloseDay = async () => {
    if (closing) return;
    setClosing(true);
    try {
      const res = await apiFetch('/api/map/history/close-day', {
        method: 'POST',
        body: JSON.stringify({ date: closeDateInput }),
      });
      const data = res.data ?? res;
      toast.success(`Day closed: ${data.date} — ${data.brands} brands, ${data.branches} branches`);
      // Refresh dates
      const datesRes = await apiFetch('/api/map/history/dates');
      const d = datesRes.data ?? datesRes ?? [];
      setDates(d);
      setSelectedDate(closeDateInput);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to close day');
    }
    setClosing(false);
  };

  // Separate overall and brand rows
  const overallRow = rows.find(r => r.brand_name === 'ALL');
  const brandRows = rows.filter(r => r.brand_name !== 'ALL');

  // Unique brands from loaded data for filter
  const availableBrands = useMemo(() => {
    const map = new Map<string, string>();
    rows.forEach(r => {
      if (r.brand_id && r.brand_name !== 'ALL') map.set(r.brand_id, r.brand_name);
    });
    return [...map.entries()];
  }, [rows]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-indigo-400" size={28} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-extrabold text-slate-900 flex items-center gap-2">
            <Archive size={20} className="text-indigo-500" /> Intelligence Archive
          </h2>
          <p className="text-xs text-slate-400 mt-1">Historical daily performance snapshots. Close each day to preserve data.</p>
        </div>

        {isAdmin && (
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-sm">
            <Calendar size={14} className="text-slate-400" />
            <input type="date" value={closeDateInput} onChange={e => setCloseDateInput(e.target.value)}
              className="text-sm border-0 outline-none bg-transparent font-mono" />
            <button onClick={handleCloseDay} disabled={closing}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 disabled:opacity-50 transition-all">
              {closing ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />} Close Day
            </button>
          </div>
        )}
      </div>

      {/* Date & Brand Selectors */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-sm">
          <Clock size={14} className="text-slate-400" />
          <select value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
            className="text-sm border-0 outline-none bg-transparent font-mono">
            {dates.length === 0 && <option value="">No data</option>}
            {dates.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>

        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-sm">
          <Filter size={14} className="text-slate-400" />
          <select value={brandFilter} onChange={e => setBrandFilter(e.target.value)}
            className="text-sm border-0 outline-none bg-transparent">
            <option value="">All Brands</option>
            <option value="ALL">Overall Rollup</option>
            {availableBrands.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        </div>
      </div>

      {loadingData ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="animate-spin text-indigo-400" size={24} />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Archive size={40} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm font-medium">No archive data for this date</p>
          <p className="text-xs mt-1">Use "Close Day" to snapshot current performance</p>
        </div>
      ) : (
        <>
          {/* Overall KPI Card */}
          {overallRow && (
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white shadow-xl">
              <div className="flex items-center gap-2 mb-4">
                <Activity size={16} className="text-indigo-300" />
                <span className="text-sm font-bold text-slate-300">Day Rollup — {overallRow.log_date}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">Sales</span>
                  <p className="text-lg font-extrabold">{overallRow.total_sales.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">Target</span>
                  <p className="text-lg font-extrabold">{overallRow.total_target.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">Ratio</span>
                  <p className="text-lg font-extrabold">{(overallRow.overall_ratio * 100).toFixed(1)}%</p>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-emerald-400">Green</span>
                  <p className="text-lg font-extrabold text-emerald-400">{overallRow.green_count}</p>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-amber-400">Amber</span>
                  <p className="text-lg font-extrabold text-amber-400">{overallRow.amber_count}</p>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-red-400">Red</span>
                  <p className="text-lg font-extrabold text-red-400">{overallRow.red_count}</p>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-yellow-300">Champions</span>
                  <p className="text-lg font-extrabold text-yellow-300">{overallRow.champion_count}</p>
                </div>
              </div>
            </div>
          )}

          {/* Brand Cards */}
          {brandRows.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {brandRows.map(row => {
                const pct = (row.overall_ratio * 100).toFixed(1);
                const color = row.overall_ratio >= 0.8 ? 'emerald' : row.overall_ratio >= 0.4 ? 'amber' : 'red';
                return (
                  <div key={row.id} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-bold text-slate-900">{row.brand_name}</h4>
                      <span className={cn('text-xs font-bold px-2.5 py-1 rounded-full',
                        color === 'emerald' ? 'bg-emerald-50 text-emerald-700' :
                        color === 'amber' ? 'bg-amber-50 text-amber-700' :
                        'bg-red-50 text-red-700')}>{pct}%</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-center mb-3">
                      <div><span className="text-[10px] text-slate-400">Sales</span><p className="text-sm font-bold">{row.total_sales.toLocaleString()}</p></div>
                      <div><span className="text-[10px] text-slate-400">Target</span><p className="text-sm font-bold">{row.total_target.toLocaleString()}</p></div>
                      <div className="flex items-center justify-center gap-1">
                        <span className="text-[10px] text-emerald-500">{row.green_count}G</span>
                        <span className="text-[10px] text-amber-500">{row.amber_count}A</span>
                        <span className="text-[10px] text-red-500">{row.red_count}R</span>
                      </div>
                    </div>
                    {/* Branch breakdown */}
                    {row.branch_data.length > 0 && (
                      <div className="border-t border-slate-100 pt-2 mt-2 max-h-40 overflow-y-auto space-y-1">
                        {row.branch_data.map((b, i) => {
                          const bRatio = b.target > 0 ? b.sales / b.target : 0;
                          return (
                            <div key={i} className="flex items-center justify-between text-[11px]">
                              <span className="text-slate-600 truncate flex-1">{b.name}</span>
                              <span className="font-mono text-slate-500 mx-2">{b.sales.toLocaleString()} / {b.target.toLocaleString()}</span>
                              <span className={cn('font-bold',
                                bRatio >= 0.8 ? 'text-emerald-600' : bRatio >= 0.4 ? 'text-amber-600' : 'text-red-600')}>
                                {(bRatio * 100).toFixed(0)}%
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Alert Feed Log */}
          {overallRow && overallRow.alert_feed.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
              <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-3">
                <AlertTriangle size={14} className="text-amber-500" /> Alert Feed Log
              </h4>
              <div className="max-h-48 overflow-y-auto space-y-1.5">
                {overallRow.alert_feed.map((alert: any, i: number) => (
                  <div key={i} className="text-[11px] text-slate-600 flex items-start gap-2">
                    <span className="text-slate-400 shrink-0">{alert.time || '—'}</span>
                    <span>{alert.message || JSON.stringify(alert)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// 7. SETTINGS TAB
// ═══════════════════════════════════════════════════════════

function SettingsTab({ user }: { user: User | null }) {
  const isAdmin = hasTabVariant(user, 'market-map', 'settings', 'admin');
  const toast = useToast();
  const [settings, setSettings] = useState<MapSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);

  // Competitor Tag Registry state
  const [tags, setTags] = useState<CompetitorTag[]>([]);
  const [tagForm, setTagForm] = useState({ name: '', logoUrl: '', color: '#ef4444' });
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [savingTag, setSavingTag] = useState(false);
  const tagFileRef = useRef<HTMLInputElement>(null);
  const [settingsSubTab, setSettingsSubTab] = useState<'general' | 'warroom' | 'tags' | 'ai'>('general');

  const loadTags = useCallback(async () => {
    if (!isRemoteMode()) return;
    try {
      const res = await apiFetch('/api/map/competitor-tags');
      setTags((res as any).data ?? []);
    } catch { /* ignore */ }
  }, []);

  const loadSettings = useCallback(async () => {
    if (!isRemoteMode()) { setLoading(false); return; }
    setError(false);
    try {
      const [settingsRes] = await Promise.all([
        apiFetch('/api/map/settings'),
        loadTags(),
      ]);
      setSettings((settingsRes as any).data ?? DEFAULT_SETTINGS);
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [loadTags]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiFetch('/api/map/settings', { method: 'PUT', body: JSON.stringify(settings) });
      toast.success('Saved', 'Map settings updated.');
    } catch (e: any) { toast.error('Failed', e.message || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const handleTagLogoUpload = async (file: File): Promise<string> => {
    const token = localStorage.getItem('dm_jwt');
    const API_BASE = (import.meta as any).env?.VITE_API_URL ?? '';
    const formData = new FormData();
    formData.append('file', file);
    formData.append('module', 'market-map');
    formData.append('entityId', 'competitor-tag');
    const res = await fetch(`${API_BASE}/api/media/upload`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) throw new Error('Upload failed');
    const data = await res.json();
    return data.url || '';
  };

  const handleSaveTag = async () => {
    if (!tagForm.name.trim()) { toast.warning('Required', 'Tag name is required.'); return; }
    setSavingTag(true);
    try {
      let logoUrl = tagForm.logoUrl;

      // Upload logo file if selected
      if (tagFileRef.current?.files?.[0]) {
        logoUrl = await handleTagLogoUpload(tagFileRef.current.files[0]);
      }

      if (editingTagId) {
        await apiFetch(`/api/map/competitor-tags/${editingTagId}`, {
          method: 'PUT',
          body: JSON.stringify({ name: tagForm.name, logoUrl, color: tagForm.color }),
        });
        toast.success('Updated', `"${tagForm.name}" updated.`);
      } else {
        await apiFetch('/api/map/competitor-tags', {
          method: 'POST',
          body: JSON.stringify({ name: tagForm.name, logoUrl, color: tagForm.color }),
        });
        toast.success('Created', `"${tagForm.name}" added.`);
      }
      setTagForm({ name: '', logoUrl: '', color: '#ef4444' });
      setEditingTagId(null);
      if (tagFileRef.current) tagFileRef.current.value = '';
      loadTags();
    } catch (e: any) { toast.error('Failed', e.message || 'Save failed.'); }
    finally { setSavingTag(false); }
  };

  const handleDeleteTag = async (id: string, name: string) => {
    if (!confirm(`Delete "${name}"? Locations using this tag will lose their tag assignment.`)) return;
    try {
      await apiFetch(`/api/map/competitor-tags/${id}`, { method: 'DELETE' });
      toast.success('Deleted', `"${name}" removed.`);
      loadTags();
    } catch (e: any) { toast.error('Failed', e.message || 'Delete failed.'); }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <Loader2 size={28} className="animate-spin text-indigo-400" />
        <p className="text-sm text-slate-400 font-medium">Loading settings…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <AlertTriangle size={28} className="text-amber-400" />
        <p className="text-sm text-slate-500 font-medium">Failed to load settings — database may be warming up.</p>
        <button onClick={() => { setLoading(true); loadSettings(); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition-colors">
          <RefreshCw size={13} /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-2xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <SettingsIcon size={20} className="text-indigo-500" /> Map Settings
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">Configure map defaults, competitor tags, and AI features.</p>
        </div>
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50 shadow-sm">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save Settings
        </button>
      </div>

      {/* Sub-Tab Navigation — pill-based horizontal nav */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
        {([
          { key: 'general' as const, label: 'General & Coverage', icon: <Globe size={14} /> },
          { key: 'warroom' as const, label: 'War Room Display', icon: <Monitor size={14} /> },
          { key: 'tags' as const, label: 'Competitor Tags', icon: <Flag size={14} /> },
          { key: 'ai' as const, label: 'AI Intelligence', icon: <Bot size={14} /> },
        ]).map(tab => (
          <button key={tab.key} onClick={() => setSettingsSubTab(tab.key)}
            className={cn('flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all whitespace-nowrap',
              settingsSubTab === tab.key
                ? 'bg-white text-indigo-700 shadow-sm'
                : 'text-slate-500 hover:text-slate-700')}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* ═══ TAB 1: General & Coverage ═══ */}
      {settingsSubTab === 'general' && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm divide-y divide-slate-100">
          {/* Default Center */}
          <div className="p-5">
            <h4 className="text-sm font-bold text-slate-900 mb-3">Default Map Center</h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-bold text-slate-500 block mb-1">Latitude</label>
                <input type="number" step="any" value={settings.default_center_lat}
                  onChange={e => setSettings(s => ({ ...s, default_center_lat: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-indigo-300" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 block mb-1">Longitude</label>
                <input type="number" step="any" value={settings.default_center_lon}
                  onChange={e => setSettings(s => ({ ...s, default_center_lon: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-indigo-300" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 block mb-1">Zoom</label>
                <input type="number" min={1} max={18} value={settings.default_zoom}
                  onChange={e => setSettings(s => ({ ...s, default_zoom: parseInt(e.target.value) || 11 }))}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-indigo-300" />
              </div>
            </div>
          </div>

          {/* Default Layers */}
          <div className="p-5 space-y-3">
            <h4 className="text-sm font-bold text-slate-900">Default Layer Visibility</h4>
            {[
              { key: 'show_internal', label: 'Show Internal Branches', desc: 'Display your own branches on the map' },
              { key: 'show_competitors', label: 'Show Competitors', desc: 'Display competitor locations' },
              { key: 'show_performance', label: 'Show Sales Performance', desc: 'Color-code branches by sales vs target' },
            ].map(item => (
              <div key={item.key} className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium text-slate-800">{item.label}</p>
                  <p className="text-[11px] text-slate-400">{item.desc}</p>
                </div>
                <button onClick={() => setSettings(s => ({ ...s, [item.key]: s[item.key as keyof MapSettings] ? 0 : 1 }))}
                  className="text-indigo-500">
                  {(settings as any)[item.key] ? <ToggleRight size={28} /> : <ToggleLeft size={28} className="text-slate-300" />}
                </button>
              </div>
            ))}
          </div>

          {/* Coverage Modeling */}
          <div className="p-5 space-y-3">
            <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Circle size={14} className="text-indigo-500" /> Coverage Modeling</h4>
            <p className="text-[11px] text-slate-400">Service radius shown around internal branches. Overlap detection highlights cannibalization.</p>
            <div>
              <label className="text-xs font-bold text-slate-500 block mb-1">Default Service Radius (meters)</label>
              <input type="number" min={100} max={50000} step={100} value={settings.default_service_radius || 3000}
                onChange={e => setSettings(s => ({ ...s, default_service_radius: parseInt(e.target.value) || 3000 }))}
                className="w-full max-w-[200px] px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-indigo-300" />
              <p className="text-[10px] text-slate-400 mt-1">Individual locations can override this in their settings.</p>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 block mb-1">Default Target Calculation Rule</label>
              <select value={settings.target_calc_rule || 'fixed'}
                onChange={e => setSettings(s => ({ ...s, target_calc_rule: e.target.value }))}
                className="w-full max-w-[200px] px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-indigo-300">
                <option value="fixed">Fixed Daily (Monthly / 30)</option>
                <option value="weighted">Weighted (Higher weekends)</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ═══ TAB 2: War Room Display ═══ */}
      {settingsSubTab === 'warroom' && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm divide-y divide-slate-100">
          {/* TV Orientation */}
          <div className="p-5 space-y-3">
            <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Monitor size={14} className="text-indigo-500" /> TV Display Orientation</h4>
            <p className="text-[11px] text-slate-400">Force screen rotation for fullscreen War Room mode on TV displays.</p>
            <div className="flex items-center gap-3">
              {(['landscape', 'portrait'] as const).map(opt => (
                <button key={opt} onClick={() => setSettings(s => ({ ...s, tv_orientation: opt }))}
                  className={cn('flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border transition-all',
                    settings.tv_orientation === opt
                      ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                      : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300')}>
                  {opt === 'landscape' ? '📺' : '📱'} {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </button>
              ))}
            </div>
            {settings.tv_orientation === 'portrait' && (
              <p className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                ⚠️ Portrait mode applies a -90° CSS rotation to the fullscreen container. Panels will reflow: Leaderboard to bottom, Alerts to top.
              </p>
            )}
          </div>

          {/* War Room Default Zoom */}
          <div className="p-5 space-y-3">
            <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Target size={14} className="text-indigo-500" /> War Room Default Zoom</h4>
            <p className="text-[11px] text-slate-400">Zoom level used when War Room camera returns to overview. Does NOT affect desktop map zoom.</p>
            <div>
              <label className="text-xs font-bold text-slate-500 block mb-1">Zoom Level (1–18)</label>
              <input type="number" min={1} max={18} value={settings.war_room_zoom ?? 12}
                onChange={e => setSettings(s => ({ ...s, war_room_zoom: parseInt(e.target.value) || 12 }))}
                className="w-full max-w-[200px] px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-indigo-300" />
              <p className="text-[10px] text-slate-400 mt-1">Lower = wider view. Recommended: 10–13 for city-wide overview.</p>
            </div>
          </div>

          {/* Auto-Cycle */}
          <div className="p-5 space-y-3">
            <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Activity size={14} className="text-indigo-500" /> Auto-Cycle Camera</h4>
            <p className="text-[11px] text-slate-400">Automatically fly to branches when live events occur. Camera returns to overview when idle.</p>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-slate-800">Enable Auto-Cycle</p>
                <p className="text-[11px] text-slate-400">Camera will automatically pan to live events in War Room</p>
              </div>
              <button onClick={() => setSettings(s => ({ ...s, auto_cycle_enabled: s.auto_cycle_enabled ? 0 : 1 }))}
                className={cn('transition-colors', settings.auto_cycle_enabled ? 'text-indigo-500' : 'text-slate-300')}>
                {settings.auto_cycle_enabled ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
              </button>
            </div>
            {settings.auto_cycle_enabled ? (
              <div>
                <label className="text-xs font-bold text-slate-500 block mb-1">Cycle Interval (seconds)</label>
                <input type="number" min={10} max={300} value={settings.auto_cycle_interval || 60}
                  onChange={e => setSettings(s => ({ ...s, auto_cycle_interval: parseInt(e.target.value) || 60 }))}
                  className="w-full max-w-[200px] px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-indigo-300" />
                <p className="text-[10px] text-slate-400 mt-1">How often the camera checks for new events to fly to.</p>
              </div>
            ) : null}
          </div>

          {/* Spotlight */}
          <div className="p-5 space-y-3">
            <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Eye size={14} className="text-indigo-500" /> Event Spotlight</h4>
            <p className="text-[11px] text-slate-400">Spotlight a branch when a new sale is detected. Camera zooms in with a toast notification.</p>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-slate-800">Enable Spotlight</p>
                <p className="text-[11px] text-slate-400">Fly to and highlight branch on new sale events</p>
              </div>
              <button onClick={() => setSettings(s => ({ ...s, spotlight_enabled: s.spotlight_enabled ? 0 : 1 }))}
                className={cn('transition-colors', settings.spotlight_enabled ? 'text-indigo-500' : 'text-slate-300')}>
                {settings.spotlight_enabled ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
              </button>
            </div>
            {settings.spotlight_enabled ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 block mb-1">Spotlight Zoom Level (1–18)</label>
                  <input type="number" min={1} max={18} value={settings.spotlight_zoom ?? 16}
                    onChange={e => setSettings(s => ({ ...s, spotlight_zoom: parseInt(e.target.value) || 16 }))}
                    className="w-full max-w-[200px] px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-indigo-300" />
                  <p className="text-[10px] text-slate-400 mt-1">How far the camera zooms in when flying to a spotlighted branch (16 = street level).</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 block mb-1">Spotlight Duration (seconds)</label>
                  <input type="number" min={3} max={30} value={settings.spotlight_duration || 8}
                    onChange={e => setSettings(s => ({ ...s, spotlight_duration: parseInt(e.target.value) || 8 }))}
                    className="w-full max-w-[200px] px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-indigo-300" />
                  <p className="text-[10px] text-slate-400 mt-1">How long the camera stays on the spotlighted branch before returning to overview.</p>
                </div>
              </div>
            ) : null}
          </div>

          {/* Event Visibility */}
          <div className="p-5 space-y-3">
            <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Activity size={14} className="text-indigo-500" /> Event Visibility</h4>
            <p className="text-[11px] text-slate-400">Control which event types appear in the War Room live feed.</p>
            {[
              { key: 'show_sales_events', label: 'Show Sales Events', desc: 'Display live sale notifications in feed & spotlight' },
              { key: 'show_competitor_events', label: 'Show Competitor Events', desc: 'Display competitor alerts in feed' },
            ].map(item => (
              <div key={item.key} className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium text-slate-800">{item.label}</p>
                  <p className="text-[11px] text-slate-400">{item.desc}</p>
                </div>
                <button onClick={() => setSettings(s => ({ ...s, [item.key]: s[item.key as keyof MapSettings] ? 0 : 1 }))}
                  className={cn('transition-colors', (settings as any)[item.key] ? 'text-indigo-500' : 'text-slate-300')}>
                  {(settings as any)[item.key] ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══ TAB 3: Competitor Tags ═══ */}
      {settingsSubTab === 'tags' && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm">
          <div className="p-5 space-y-4">
            <div>
              <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Flag size={14} className="text-red-500" /> Competitor Tags & Logos
              </h4>
              <p className="text-[11px] text-slate-400 mt-1">Define competitor categories with custom logos for map pins.</p>
            </div>

            {/* Existing tags list */}
            {tags.length > 0 && (
              <div className="space-y-2">
                {tags.map(tag => (
                  <div key={tag.id} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg group">
                    {tag.logo_url ? (
                      <img src={getMediaUrl(tag.logo_url)} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-slate-200 flex-shrink-0" />
                    ) : (
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-black flex-shrink-0" style={{ background: tag.color || '#ef4444' }}>
                        {tag.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{tag.name}</p>
                      <p className="text-[10px] text-slate-400">{tag.logo_url ? 'Custom logo' : 'Letter initial'}</p>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => {
                        setEditingTagId(tag.id);
                        setTagForm({ name: tag.name, logoUrl: tag.logo_url, color: tag.color || '#ef4444' });
                      }}
                        className="p-1.5 hover:bg-white rounded-lg text-slate-400 hover:text-indigo-600 transition-colors">
                        <Edit2 size={13} />
                      </button>
                      <button onClick={() => handleDeleteTag(tag.id, tag.name)}
                        className="p-1.5 hover:bg-white rounded-lg text-slate-400 hover:text-red-600 transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tags.length === 0 && (
              <div className="text-center py-4 bg-slate-50 rounded-lg">
                <Flag size={20} className="mx-auto text-slate-300 mb-2" />
                <p className="text-xs text-slate-400">No competitor tags yet. Add your first below.</p>
              </div>
            )}

            {/* Add / Edit form */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
              <p className="text-xs font-bold text-slate-600">{editingTagId ? 'Edit Tag' : 'Add New Tag'}</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-slate-500 block mb-1">Tag Name *</label>
                  <input value={tagForm.name} onChange={e => setTagForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Pizza Rival"
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:border-indigo-300 outline-none" />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-500 block mb-1">Brand Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={tagForm.color} onChange={e => setTagForm(f => ({ ...f, color: e.target.value }))}
                      className="w-8 h-8 rounded cursor-pointer border border-slate-200" />
                    <span className="text-xs text-slate-400">{tagForm.color}</span>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold text-slate-500 block mb-1">Logo Image</label>
                <input ref={tagFileRef} type="file" accept="image/*"
                  className="w-full text-xs text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-indigo-50 file:text-indigo-600 hover:file:bg-indigo-100" />
                {tagForm.logoUrl && (
                  <div className="flex items-center gap-2 mt-2">
                    <img src={getMediaUrl(tagForm.logoUrl)} alt="" className="w-8 h-8 rounded-full object-cover border border-slate-200" />
                    <button onClick={() => setTagForm(f => ({ ...f, logoUrl: '' }))} className="text-[10px] text-red-500 hover:underline">Remove</button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={handleSaveTag} disabled={savingTag}
                  className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50">
                  {savingTag ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                  {editingTagId ? 'Update Tag' : 'Add Tag'}
                </button>
                {editingTagId && (
                  <button onClick={() => { setEditingTagId(null); setTagForm({ name: '', logoUrl: '', color: '#ef4444' }); if (tagFileRef.current) tagFileRef.current.value = ''; }}
                    className="px-3 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 transition-colors">
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ TAB 4: AI Intelligence ═══ */}
      {settingsSubTab === 'ai' && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm">
          <div className="p-5 space-y-3">
            <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Bot size={14} className="text-violet-500" /> AI Market Scout</h4>
            <p className="text-[11px] text-slate-400">AI-powered competitor discovery and market intelligence features.</p>
            {/* Google Places Auto-Discovery */}
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-slate-800">Google Places Auto-Discovery</p>
                <p className="text-[11px] text-slate-400">Automatically discover and import nearby competitors via Google Places API</p>
              </div>
              <button onClick={() => setSettings(s => ({ ...s, ai_google_places: s.ai_google_places ? 0 : 1 }))}
                className={cn('transition-colors', settings.ai_google_places ? 'text-emerald-500' : 'text-slate-300')}>
                {settings.ai_google_places ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
              </button>
            </div>
            {/* Predictive Sales Forecasting */}
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-slate-800">Predictive Sales Forecasting</p>
                <p className="text-[11px] text-slate-400">AI-powered sales predictions based on historical data (Blind Math — no business names sent to AI)</p>
              </div>
              <button onClick={() => setSettings(s => ({ ...s, ai_predictive_sales: s.ai_predictive_sales ? 0 : 1 }))}
                className={cn('transition-colors', settings.ai_predictive_sales ? 'text-emerald-500' : 'text-slate-300')}>
                {settings.ai_predictive_sales ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
